import { lazy, Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { AppShell } from './app/AppShell';
import { journeyTabLabels, journeyUiLabels, roadbookLandmarkLabels } from './app/accessibility-labels';
import { initialJourneyState, reduceJourney } from './app/journey-state';
import { reducedMotionRequested } from './app/motion';
import { shouldStartNavigation } from './app/navigation-transition';
import {
  FUEL_PREFERENCES_KEY,
  LANGUAGE_PREFERENCES_KEY,
  UNIT_PREFERENCES_KEY,
  persistFuelPreferences,
  persistLanguagePreference,
  persistUiModePreference,
  persistUnitPreference,
  selectAndActivateFuelType
} from './app/preferences';
import { readTheme, resolveTheme } from './app/theme';
import { normalizeSavedRouteSelection, orderActiveRouteFeatures, RouteAlternatives, toggleRouteSelection } from './journey/RouteAlternatives';
import { NextManeuver } from './journey/NextManeuver';
import { JourneyTabs, tabAfterFocusedRouteChange, type JourneyTab } from './journey/JourneyTabs';
import { Roadbook } from './journey/Roadbook';
import { RoutePlanner, type RouteInputs } from './journey/RoutePlanner';
import { buildRouteSummary, RouteSummary } from './journey/RouteSummary';
import { RouteContextCards } from './journey/RouteContextCards';
import {
  fingerprintManeuverRoute,
  formatLiveTrafficSummary,
  maneuverProgressForRoute,
  normalizeLiveTraffic,
  persistManeuverProgress,
  rankRouteContext,
  type FocusedManeuverProgress,
  type RouteContext
} from './journey/route-context';
import { TravelSheet } from './journey/TravelSheet';
import { MapCanvas } from './map/MapCanvas';
import { type MapAdapter, type MapMarkerHandle } from './map/map-adapter';
import { filterFuelNearRoutes } from './map/journey-layer-controller';
import { boundsForRoutes } from './map/route-layer-controller';
import { cleanupTrackingRuntime } from './map/tracking-lifecycle';
import {
  type WeatherGrid
} from './map/weather-layer-controller';
import { resolveNextInstruction } from './maneuvers/progress';
import { CompactLongitudinalProfile } from './profile/CompactLongitudinalProfile';
import type { ProfileCursor, ProfileTemperature, ProfileViewport } from './profile/profile-model';
import type { MapStyleChoice } from './tools/MapTools';
import { toolLabels } from './tools/labels';
import { LANGUAGE_CODES } from './types';
import type { BasemapOverride, Coord3, DifficultyMode, FuelGeoJson, FuelType, LanguageCode, PriceMode, ProfileColorMode, RouteFeature, RoutesGeoJson, TollGeoJson, TrackingMode, TrafficStyle, UiMode, UnitSystem, WindLevel } from './types';

const BASE = import.meta.env.BASE_URL || '/map/';
const LazyExpandedProfile = lazy(() => import('./profile/LongitudinalProfile').then((module) => ({ default: module.LongitudinalProfile })));
const LazyToolDrawer = lazy(() => import('./tools/ToolDrawer').then((module) => ({ default: module.ToolDrawer })));
const asset = (path: string) => `${BASE}${path.replace(/^\//, '')}`;
const savedRoutesKey = 'beachlab.map.savedRoutes.v1';
const savedDynamicRoutesKey = 'beachlab.map.dynamicRoutes.v1';
const trafficStyles = [
  { value: 'relative0', label: 'Flujo relativo' },
  { value: 'relative0-dark', label: 'Flujo oscuro' },
  { value: 'relative-delay', label: 'Retardo relativo' },
  { value: 'absolute', label: 'Velocidad absoluta' }
] as const;
const rainViewerApiUrl = 'https://api.rainviewer.com/public/weather-maps.json';
const mapThemes = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'grayscale', label: 'Grayscale' },
  { value: 'bright', label: 'Bright' },
  { value: 'orbita', label: 'Topo' },
  { value: 'gaia', label: 'Gaia' },
  { value: 'black', label: 'Black' },
  { value: 'white', label: 'White' }
] as const;
const difficultyModes = [
  { value: 'slope', label: 'Pendiente' },
  { value: 'curves', label: 'Curvas' },
  { value: 'hairpins', label: 'Tornanti' }
] as const;
const languageCodes = LANGUAGE_CODES;

const routePalette = ['#2dd4bf', '#60a5fa', '#f97316', '#a78bfa', '#f43f5e', '#22c55e', '#eab308'];
const routeProfileColorModes: Array<{ value: ProfileColorMode; label: string }> = [
  { value: 'elevation', label: 'Elevation' },
  { value: 'max_speed', label: 'Max speed' },
  { value: 'average_speed', label: 'Average speed' },
  { value: 'road_class', label: 'Road class' },
  { value: 'surface', label: 'Surface' },
  { value: 'country', label: 'Country' },
  { value: 'toll', label: 'Toll' },
  { value: 'road_environment', label: 'Environment' },
  { value: 'road_access', label: 'Access' },
  { value: 'lanes', label: 'Lanes' }
];
const tempStops: Array<[number, [number, number, number, number?]]> = [
  [-40, [37, 41, 130]],
  [-20, [65, 182, 196]],
  [0, [127, 205, 150]],
  [10, [199, 233, 100]],
  [20, [255, 237, 74]],
  [30, [254, 153, 41]],
  [40, [217, 72, 1]],
  [50, [153, 0, 102]]
];
const precipStops: Array<[number, [number, number, number, number?]]> = [
  [0, [0, 0, 0, 0]],
  [0.00002, [170, 210, 255, 42]],
  [0.0001, [130, 190, 245, 86]],
  [0.0003, [90, 160, 230, 126]],
  [0.001, [50, 110, 210, 176]],
  [0.003, [30, 60, 180, 218]],
  [0.01, [120, 40, 180, 246]]
];
const pressureStops: Array<[number, [number, number, number, number?]]> = [
  [96000, [58, 80, 158, 56]],
  [99000, [77, 148, 166, 70]],
  [101300, [245, 213, 97, 86]],
  [103500, [215, 96, 92, 106]]
];
const fuelTypeLabels: Record<FuelType, string> = {
  diesel: 'Diesel',
  gasoline_95: 'Gasolina 95',
  gasoline_98: 'Gasolina 98',
  diesel_premium: 'Diesel+',
  lpg: 'LPG'
};
type WeatherState = { temp?: WeatherGrid; precip?: WeatherGrid; pressure?: WeatherGrid };
type TempLegend = { min: number; max: number } | null;
type RainRadarFrame = { host: string; path: string; time: number; generated?: number };
type RainRadarCoverage = { percent: number; tiles: number; maxDbz?: number } | null;
type RainRadarSamples = Record<number, Record<number, number>>;
type FuelHistogramState = { prices: number[]; mixedUnits: boolean } | null;
type MapTheme = typeof mapThemes[number]['value'];
type StarfieldView = { lng: number; lat: number; bearing: number; pitch: number };
type CelestialStar = {
  x: number;
  y: number;
  z: number;
  size: number;
  opacity: number;
  phase: number;
  twinkle: number;
};
type TrafficEta = {
  status: string;
  route_num: number;
  tomtom?: {
    travel_time_s?: number;
    traffic_delay_s?: number;
    historic_traffic_travel_time_s?: number;
    no_traffic_travel_time_s?: number;
  };
};
type DifficultySummary = {
  route_num: number;
  steep_climb_km?: number;
  steep_descent_km?: number;
  curvy_km?: number;
  very_curvy_km?: number;
  hairpin_count?: number;
  high_confidence_hairpin_count?: number;
};

const uiText = {
  EN: {
    loadingMap: 'Loading map',
    mapReady: 'Map ready',
    myLocation: 'my location',
    mapControls: 'Map controls',
    journey: 'Journey',
    tools: 'Tools',
    closeJourney: 'Close journey',
    closeTools: 'Close tools',
    language: 'Language',
    roadbook: 'Roadbook',
    originPlaceholder: 'Origin: my location, map or POI',
    viaPlaceholder: 'Via',
    optional: 'optional',
    destinationPlaceholder: 'Destination: POI or lat,lon',
    addVia: 'Add via',
    remove: 'Remove',
    calculating: 'Calculating...',
    calculateRoute: 'Calculate route',
    fit: 'Fit',
    save: 'Save',
    load: 'Load',
    fuel: 'Fuel',
    tolls: 'Tolls',
    temperature: 'Temperature',
    rainRadar: 'Rain radar',
    wind: 'Wind',
    fuelColor: 'Fuel color',
    relative: 'Relative',
    absolute: 'Absolute',
    units: 'Units',
    metric: 'Metric',
    imperial: 'Imperial',
    min: 'Min',
    max: 'Max',
    nearRoute: 'Near route',
    price: 'Price',
    localCurrency: 'Local currency / unit',
    eurNormalized: 'Normalized EUR/L',
    map: 'Map',
    layers: 'Layers',
    trafficTomTom: 'TomTom traffic',
    trafficStyle: 'Traffic style',
    etaLiveRoutes: 'Live ETA on routes',
    munichBaustellen: 'Munich Baustellen',
    hillshadeMapterhorn: 'Mapterhorn hillshade',
    terrain3d: '3D terrain',
    contours: 'Contours',
    analysis: 'Analysis',
    routeDifficulty: 'Route difficulty',
    mode: 'Mode',
    weather: 'Weather',
    windLevel: 'Wind level',
    surface: 'Surface',
    pressure: 'Pressure',
    legacyHtml: 'Legacy HTML',
    rainIntensity: 'Rain radar intensity',
    visibleTemperatureScale: 'Visible temperature scale',
    noVisibleRain: 'No visible rain in view',
    weakEchoes: 'Weak echoes in view',
    radarRain: 'Rain radar',
    dynamic: 'Dynamic',
    elevationProfile: 'Elevation profile',
    activeRoutesElevation: 'Active route elevation',
    expandProfile: 'Expand profile',
    collapseProfile: 'Collapse profile',
    profileColorMode: 'Profile color mode',
    noRadarEchoRoute: 'No radar echo over route',
    routesSaved: 'Routes saved on this device',
    noSavedRoutes: 'No saved route set',
    locationUnavailable: 'Location unavailable',
    locationOff: 'Location off',
    locationDenied: 'Location denied',
    destinationRequired: 'Destination required',
    routeApiUnavailable: 'Route API unavailable',
    routeNotFound: 'Route not found',
    dynamicRouteCalculated: 'Dynamic route calculated',
    routeCalculationFailed: 'Route calculation failed',
    dynamicRoute: 'dynamic',
    curves: 'curves',
    follow: 'Follow',
    locate: 'Locate'
  },
  DE: {
    loadingMap: 'Karte wird geladen',
    mapReady: 'Karte bereit',
    myLocation: 'mein Standort',
    mapControls: 'Kartensteuerung',
    journey: 'Reise',
    tools: 'Werkzeuge',
    closeJourney: 'Reise schließen',
    closeTools: 'Werkzeuge schließen',
    language: 'Sprache',
    roadbook: 'Roadbook',
    originPlaceholder: 'Start: mein Standort, Karte oder POI',
    viaPlaceholder: 'Zwischenziel',
    optional: 'optional',
    destinationPlaceholder: 'Ziel: POI oder lat,lon',
    addVia: 'Zwischenziel hinzufügen',
    remove: 'Entfernen',
    calculating: 'Berechnung...',
    calculateRoute: 'Route berechnen',
    fit: 'Einpassen',
    save: 'Speichern',
    load: 'Laden',
    fuel: 'Kraftstoff',
    tolls: 'Maut',
    temperature: 'Temperatur',
    rainRadar: 'Regenradar',
    wind: 'Wind',
    fuelColor: 'Kraftstofffarbe',
    relative: 'Relativ',
    absolute: 'Absolut',
    units: 'Einheiten',
    metric: 'Metrisch',
    imperial: 'Imperial',
    min: 'Min',
    max: 'Max',
    nearRoute: 'Nahe Route',
    price: 'Preis',
    localCurrency: 'Lokale Währung / Einheit',
    eurNormalized: 'EUR/L normalisiert',
    map: 'Karte',
    layers: 'Ebenen',
    trafficTomTom: 'TomTom-Verkehr',
    trafficStyle: 'Verkehrsstil',
    etaLiveRoutes: 'Live-ETA auf Routen',
    munichBaustellen: 'Münchner Baustellen',
    hillshadeMapterhorn: 'Mapterhorn-Schummerung',
    terrain3d: '3D-Gelände',
    contours: 'Höhenlinien',
    analysis: 'Analyse',
    routeDifficulty: 'Routenschwierigkeit',
    mode: 'Modus',
    weather: 'Wetter',
    windLevel: 'Windniveau',
    surface: 'Boden',
    pressure: 'Luftdruck',
    legacyHtml: 'Legacy HTML',
    rainIntensity: 'Regenradar-Intensität',
    visibleTemperatureScale: 'Sichtbare Temperaturskala',
    noVisibleRain: 'Kein sichtbarer Regen im Ausschnitt',
    weakEchoes: 'Schwache Echos im Ausschnitt',
    radarRain: 'Regenradar',
    dynamic: 'Dynamisch',
    elevationProfile: 'Höhenprofil',
    activeRoutesElevation: 'Höhe der aktiven Route',
    expandProfile: 'Profil ausklappen',
    collapseProfile: 'Profil einklappen',
    profileColorMode: 'Profil-Farbmodus',
    noRadarEchoRoute: 'Kein Radarecho auf der Route',
    routesSaved: 'Routen auf diesem Gerät gespeichert',
    noSavedRoutes: 'Keine gespeicherte Route',
    locationUnavailable: 'Standort nicht verfügbar',
    locationOff: 'Standort aus',
    locationDenied: 'Standort abgelehnt',
    destinationRequired: 'Ziel erforderlich',
    routeApiUnavailable: 'Routen-API nicht verfügbar',
    routeNotFound: 'Route nicht gefunden',
    dynamicRouteCalculated: 'Dynamische Route berechnet',
    routeCalculationFailed: 'Routenberechnung fehlgeschlagen',
    dynamicRoute: 'dynamisch',
    curves: 'Kurven',
    follow: 'Folgen',
    locate: 'Orten'
  },
  ES: {
    loadingMap: 'Cargando mapa',
    mapReady: 'Mapa listo',
    myLocation: 'mi ubicación',
    mapControls: 'Controles del mapa',
    journey: 'Viaje',
    tools: 'Herramientas',
    closeJourney: 'Cerrar viaje',
    closeTools: 'Cerrar herramientas',
    language: 'Idioma',
    roadbook: 'Roadbook',
    originPlaceholder: 'Origen: mi ubicación, mapa o POI',
    viaPlaceholder: 'Via',
    optional: 'opcional',
    destinationPlaceholder: 'Destino: POI o lat,lon',
    addVia: 'Añadir via',
    remove: 'Quitar',
    calculating: 'Calculando...',
    calculateRoute: 'Calcular ruta',
    fit: 'Encajar',
    save: 'Guardar',
    load: 'Cargar',
    fuel: 'Combustible',
    tolls: 'Peajes',
    temperature: 'Temperatura',
    rainRadar: 'Radar lluvia',
    wind: 'Viento',
    fuelColor: 'Color combustible',
    relative: 'Relativo',
    absolute: 'Absoluto',
    units: 'Unidades',
    metric: 'Métrico',
    imperial: 'Imperial',
    min: 'Min',
    max: 'Max',
    nearRoute: 'Cerca de ruta',
    price: 'Precio',
    localCurrency: 'Moneda local / unidad',
    eurNormalized: 'EUR/L normalizado',
    map: 'Mapa',
    layers: 'Capas',
    trafficTomTom: 'Tráfico TomTom',
    trafficStyle: 'Estilo tráfico',
    etaLiveRoutes: 'ETA live en rutas',
    munichBaustellen: 'Munich Baustellen',
    hillshadeMapterhorn: 'Hillshade Mapterhorn',
    terrain3d: 'Terreno 3D',
    contours: 'Contornos',
    analysis: 'Análisis',
    routeDifficulty: 'Dificultad ruta',
    mode: 'Modo',
    weather: 'Tiempo',
    windLevel: 'Nivel viento',
    surface: 'Superficie',
    pressure: 'Presión',
    legacyHtml: 'Legacy HTML',
    rainIntensity: 'Intensidad radar lluvia',
    visibleTemperatureScale: 'Escala visible de temperatura',
    noVisibleRain: 'Sin lluvia visible en vista',
    weakEchoes: 'Ecos débiles en vista',
    radarRain: 'Radar lluvia',
    dynamic: 'Dinámico',
    elevationProfile: 'Perfil de elevación',
    activeRoutesElevation: 'Elevación de ruta activa',
    expandProfile: 'Expandir perfil',
    collapseProfile: 'Plegar perfil',
    profileColorMode: 'Modo de color del perfil',
    noRadarEchoRoute: 'Sin eco radar sobre ruta',
    routesSaved: 'Rutas guardadas en este dispositivo',
    noSavedRoutes: 'No hay rutas guardadas',
    locationUnavailable: 'Ubicación no disponible',
    locationOff: 'Ubicación desactivada',
    locationDenied: 'Ubicación denegada',
    destinationRequired: 'Destino obligatorio',
    routeApiUnavailable: 'API de rutas no disponible',
    routeNotFound: 'Ruta no encontrada',
    dynamicRouteCalculated: 'Ruta dinámica calculada',
    routeCalculationFailed: 'Error calculando ruta',
    dynamicRoute: 'dinámica',
    curves: 'curvas',
    follow: 'Seguir',
    locate: 'Localizar'
  },
  IT: {
    loadingMap: 'Caricamento mappa',
    mapReady: 'Mappa pronta',
    myLocation: 'mia posizione',
    mapControls: 'Controlli mappa',
    journey: 'Viaggio',
    tools: 'Strumenti',
    closeJourney: 'Chiudi viaggio',
    closeTools: 'Chiudi strumenti',
    language: 'Lingua',
    roadbook: 'Roadbook',
    originPlaceholder: 'Origine: mia posizione, mappa o POI',
    viaPlaceholder: 'Via',
    optional: 'opzionale',
    destinationPlaceholder: 'Destinazione: POI o lat,lon',
    addVia: 'Aggiungi via',
    remove: 'Rimuovi',
    calculating: 'Calcolo...',
    calculateRoute: 'Calcola percorso',
    fit: 'Inquadra',
    save: 'Salva',
    load: 'Carica',
    fuel: 'Carburante',
    tolls: 'Pedaggi',
    temperature: 'Temperatura',
    rainRadar: 'Radar pioggia',
    wind: 'Vento',
    fuelColor: 'Colore carburante',
    relative: 'Relativo',
    absolute: 'Assoluto',
    units: 'Unità',
    metric: 'Metrico',
    imperial: 'Imperiale',
    min: 'Min',
    max: 'Max',
    nearRoute: 'Vicino al percorso',
    price: 'Prezzo',
    localCurrency: 'Valuta locale / unità',
    eurNormalized: 'EUR/L normalizzato',
    map: 'Mappa',
    layers: 'Livelli',
    trafficTomTom: 'Traffico TomTom',
    trafficStyle: 'Stile traffico',
    etaLiveRoutes: 'ETA live sui percorsi',
    munichBaustellen: 'Cantieri Monaco',
    hillshadeMapterhorn: 'Ombreggiatura Mapterhorn',
    terrain3d: 'Terreno 3D',
    contours: 'Curve di livello',
    analysis: 'Analisi',
    routeDifficulty: 'Difficoltà percorso',
    mode: 'Modalità',
    weather: 'Meteo',
    windLevel: 'Livello vento',
    surface: 'Superficie',
    pressure: 'Pressione',
    legacyHtml: 'Legacy HTML',
    rainIntensity: 'Intensità radar pioggia',
    visibleTemperatureScale: 'Scala temperatura visibile',
    noVisibleRain: 'Nessuna pioggia visibile nella vista',
    weakEchoes: 'Echi deboli nella vista',
    radarRain: 'Radar pioggia',
    dynamic: 'Dinamico',
    elevationProfile: 'Profilo altimetrico',
    activeRoutesElevation: 'Altimetria percorso attivo',
    expandProfile: 'Espandi profilo',
    collapseProfile: 'Comprimi profilo',
    profileColorMode: 'Modalità colore profilo',
    noRadarEchoRoute: 'Nessun eco radar sul percorso',
    routesSaved: 'Percorsi salvati su questo dispositivo',
    noSavedRoutes: 'Nessun percorso salvato',
    locationUnavailable: 'Posizione non disponibile',
    locationOff: 'Posizione disattivata',
    locationDenied: 'Posizione negata',
    destinationRequired: 'Destinazione obbligatoria',
    routeApiUnavailable: 'API percorsi non disponibile',
    routeNotFound: 'Percorso non trovato',
    dynamicRouteCalculated: 'Percorso dinamico calcolato',
    routeCalculationFailed: 'Calcolo percorso non riuscito',
    dynamicRoute: 'dinamico',
    curves: 'curve',
    follow: 'Segui',
    locate: 'Localizza'
  },
  FR: {
    loadingMap: 'Chargement de la carte',
    mapReady: 'Carte prête',
    myLocation: 'ma position',
    mapControls: 'Commandes de carte',
    journey: 'Voyage',
    tools: 'Outils',
    closeJourney: 'Fermer le voyage',
    closeTools: 'Fermer les outils',
    language: 'Langue',
    roadbook: 'Roadbook',
    originPlaceholder: 'Départ : ma position, carte ou POI',
    viaPlaceholder: 'Via',
    optional: 'optionnel',
    destinationPlaceholder: 'Destination : POI ou lat,lon',
    addVia: 'Ajouter via',
    remove: 'Retirer',
    calculating: 'Calcul...',
    calculateRoute: 'Calculer itinéraire',
    fit: 'Ajuster',
    save: 'Sauver',
    load: 'Charger',
    fuel: 'Carburant',
    tolls: 'Péages',
    temperature: 'Température',
    rainRadar: 'Radar pluie',
    wind: 'Vent',
    fuelColor: 'Couleur carburant',
    relative: 'Relatif',
    absolute: 'Absolu',
    units: 'Unités',
    metric: 'Métrique',
    imperial: 'Impérial',
    min: 'Min',
    max: 'Max',
    nearRoute: "Près de l'itinéraire",
    price: 'Prix',
    localCurrency: 'Monnaie locale / unité',
    eurNormalized: 'EUR/L normalisé',
    map: 'Carte',
    layers: 'Couches',
    trafficTomTom: 'Trafic TomTom',
    trafficStyle: 'Style trafic',
    etaLiveRoutes: 'ETA live sur itinéraires',
    munichBaustellen: 'Chantiers Munich',
    hillshadeMapterhorn: 'Ombrage Mapterhorn',
    terrain3d: 'Terrain 3D',
    contours: 'Courbes de niveau',
    analysis: 'Analyse',
    routeDifficulty: "Difficulté d'itinéraire",
    mode: 'Mode',
    weather: 'Météo',
    windLevel: 'Niveau du vent',
    surface: 'Surface',
    pressure: 'Pression',
    legacyHtml: 'Legacy HTML',
    rainIntensity: 'Intensité radar pluie',
    visibleTemperatureScale: 'Échelle température visible',
    noVisibleRain: 'Aucune pluie visible dans la vue',
    weakEchoes: 'Échos faibles dans la vue',
    radarRain: 'Radar pluie',
    dynamic: 'Dynamique',
    elevationProfile: "Profil d'altitude",
    activeRoutesElevation: "Altitude de l'itinéraire actif",
    expandProfile: 'Déplier le profil',
    collapseProfile: 'Replier le profil',
    profileColorMode: 'Mode couleur du profil',
    noRadarEchoRoute: "Aucun écho radar sur l'itinéraire",
    routesSaved: 'Itinéraires sauvés sur cet appareil',
    noSavedRoutes: 'Aucun itinéraire sauvé',
    locationUnavailable: 'Position indisponible',
    locationOff: 'Position désactivée',
    locationDenied: 'Position refusée',
    destinationRequired: 'Destination requise',
    routeApiUnavailable: 'API itinéraires indisponible',
    routeNotFound: 'Itinéraire introuvable',
    dynamicRouteCalculated: 'Itinéraire dynamique calculé',
    routeCalculationFailed: "Échec du calcul d'itinéraire",
    dynamicRoute: 'dynamique',
    curves: 'virages',
    follow: 'Suivre',
    locate: 'Localiser'
  }
} as const;

type UiTextKey = keyof typeof uiText.EN;

const profileModeLabelsByLanguage: Record<LanguageCode, Record<ProfileColorMode, string>> = {
  EN: {
    elevation: 'Elevation',
    max_speed: 'Max speed',
    average_speed: 'Average speed',
    road_class: 'Road class',
    surface: 'Surface',
    country: 'Country',
    toll: 'Toll',
    road_environment: 'Environment',
    road_access: 'Access',
    lanes: 'Lanes'
  },
  DE: {
    elevation: 'Höhe',
    max_speed: 'Max. Tempo',
    average_speed: 'Durchschnittstempo',
    road_class: 'Straßenklasse',
    surface: 'Belag',
    country: 'Land',
    toll: 'Maut',
    road_environment: 'Umgebung',
    road_access: 'Zugang',
    lanes: 'Spuren'
  },
  ES: {
    elevation: 'Elevación',
    max_speed: 'Velocidad máxima',
    average_speed: 'Velocidad media',
    road_class: 'Tipo de vía',
    surface: 'Superficie',
    country: 'País',
    toll: 'Peaje',
    road_environment: 'Entorno',
    road_access: 'Acceso',
    lanes: 'Carriles'
  },
  IT: {
    elevation: 'Altitudine',
    max_speed: 'Velocità max',
    average_speed: 'Velocità media',
    road_class: 'Classe strada',
    surface: 'Superficie',
    country: 'Paese',
    toll: 'Pedaggio',
    road_environment: 'Ambiente',
    road_access: 'Accesso',
    lanes: 'Corsie'
  },
  FR: {
    elevation: 'Altitude',
    max_speed: 'Vitesse max',
    average_speed: 'Vitesse moyenne',
    road_class: 'Classe de route',
    surface: 'Revêtement',
    country: 'Pays',
    toll: 'Péage',
    road_environment: 'Environnement',
    road_access: 'Accès',
    lanes: 'Voies'
  }
};

const trafficStyleLabelsByLanguage: Record<LanguageCode, Record<TrafficStyle, string>> = {
  EN: { relative0: 'Relative flow', 'relative0-dark': 'Dark flow', 'relative-delay': 'Relative delay', absolute: 'Absolute speed' },
  DE: { relative0: 'Relativer Fluss', 'relative0-dark': 'Dunkler Fluss', 'relative-delay': 'Relative Verzögerung', absolute: 'Absolute Geschwindigkeit' },
  ES: { relative0: 'Flujo relativo', 'relative0-dark': 'Flujo oscuro', 'relative-delay': 'Retardo relativo', absolute: 'Velocidad absoluta' },
  IT: { relative0: 'Flusso relativo', 'relative0-dark': 'Flusso scuro', 'relative-delay': 'Ritardo relativo', absolute: 'Velocità assoluta' },
  FR: { relative0: 'Flux relatif', 'relative0-dark': 'Flux sombre', 'relative-delay': 'Retard relatif', absolute: 'Vitesse absolue' }
};

const difficultyModeLabelsByLanguage: Record<LanguageCode, Record<DifficultyMode, string>> = {
  EN: { slope: 'Slope', curves: 'Curves', hairpins: 'Hairpins' },
  DE: { slope: 'Steigung', curves: 'Kurven', hairpins: 'Kehren' },
  ES: { slope: 'Pendiente', curves: 'Curvas', hairpins: 'Tornanti' },
  IT: { slope: 'Pendenza', curves: 'Curve', hairpins: 'Tornanti' },
  FR: { slope: 'Pente', curves: 'Virages', hairpins: 'Épingles' }
};

const rainViewerUniversalBlueStops = [
  { dbz: 10, color: '#cec08796', rgba: [206, 192, 135, 150] },
  { dbz: 11, color: '#d2c48ba0', rgba: [210, 196, 139, 160] },
  { dbz: 12, color: '#d6c88faa', rgba: [214, 200, 143, 170] },
  { dbz: 13, color: '#dacc93b4', rgba: [218, 204, 147, 180] },
  { dbz: 14, color: '#ded097be', rgba: [222, 208, 151, 190] },
  { dbz: 15, color: '#88ddeeff', rgba: [136, 221, 238, 255] },
  { dbz: 16, color: '#6cd1ebff', rgba: [108, 209, 235, 255] },
  { dbz: 17, color: '#51c5e8ff', rgba: [81, 197, 232, 255] },
  { dbz: 18, color: '#36bae5ff', rgba: [54, 186, 229, 255] },
  { dbz: 19, color: '#1baee2ff', rgba: [27, 174, 226, 255] },
  { dbz: 20, color: '#00a3e0ff', rgba: [0, 163, 224, 255] },
  { dbz: 21, color: '#009ad5ff', rgba: [0, 154, 213, 255] },
  { dbz: 22, color: '#0091caff', rgba: [0, 145, 202, 255] },
  { dbz: 23, color: '#0088bfff', rgba: [0, 136, 191, 255] },
  { dbz: 24, color: '#007fb4ff', rgba: [0, 127, 180, 255] },
  { dbz: 25, color: '#0077aaff', rgba: [0, 119, 170, 255] },
  { dbz: 26, color: '#0070a3ff', rgba: [0, 112, 163, 255] },
  { dbz: 27, color: '#00699cff', rgba: [0, 105, 156, 255] },
  { dbz: 28, color: '#006295ff', rgba: [0, 98, 149, 255] },
  { dbz: 29, color: '#005b8eff', rgba: [0, 91, 142, 255] },
  { dbz: 30, color: '#005588ff', rgba: [0, 85, 136, 255] },
  { dbz: 31, color: '#005180ff', rgba: [0, 81, 128, 255] },
  { dbz: 32, color: '#004e78ff', rgba: [0, 78, 120, 255] },
  { dbz: 33, color: '#004a70ff', rgba: [0, 74, 112, 255] },
  { dbz: 34, color: '#004768ff', rgba: [0, 71, 104, 255] },
  { dbz: 35, color: '#ffee00ff', rgba: [255, 238, 0, 255] },
  { dbz: 36, color: '#ffe000ff', rgba: [255, 224, 0, 255] },
  { dbz: 37, color: '#ffd200ff', rgba: [255, 210, 0, 255] },
  { dbz: 38, color: '#ffc500ff', rgba: [255, 197, 0, 255] },
  { dbz: 39, color: '#ffb700ff', rgba: [255, 183, 0, 255] },
  { dbz: 40, color: '#ffaa00ff', rgba: [255, 170, 0, 255] },
  { dbz: 41, color: '#ff9f00ff', rgba: [255, 159, 0, 255] },
  { dbz: 42, color: '#ff9500ff', rgba: [255, 149, 0, 255] },
  { dbz: 43, color: '#ff8b00ff', rgba: [255, 139, 0, 255] },
  { dbz: 44, color: '#ff8100ff', rgba: [255, 129, 0, 255] },
  { dbz: 45, color: '#ff4400ff', rgba: [255, 68, 0, 255] },
  { dbz: 46, color: '#f23600ff', rgba: [242, 54, 0, 255] },
  { dbz: 47, color: '#e62800ff', rgba: [230, 40, 0, 255] },
  { dbz: 48, color: '#d91b00ff', rgba: [217, 27, 0, 255] },
  { dbz: 49, color: '#cd0d00ff', rgba: [205, 13, 0, 255] },
  { dbz: 50, color: '#c10000ff', rgba: [193, 0, 0, 255] },
  { dbz: 51, color: '#a80000ff', rgba: [168, 0, 0, 255] },
  { dbz: 52, color: '#8f0000ff', rgba: [143, 0, 0, 255] },
  { dbz: 53, color: '#760000ff', rgba: [118, 0, 0, 255] },
  { dbz: 54, color: '#5d0000ff', rgba: [93, 0, 0, 255] },
  { dbz: 55, color: '#ffaaffff', rgba: [255, 170, 255, 255] },
  { dbz: 56, color: '#ff9fffff', rgba: [255, 159, 255, 255] },
  { dbz: 57, color: '#ff95ffff', rgba: [255, 149, 255, 255] },
  { dbz: 58, color: '#ff8bffff', rgba: [255, 139, 255, 255] },
  { dbz: 59, color: '#ff81ffff', rgba: [255, 129, 255, 255] },
  { dbz: 60, color: '#ff77ffff', rgba: [255, 119, 255, 255] },
  { dbz: 61, color: '#ff6cffff', rgba: [255, 108, 255, 255] },
  { dbz: 62, color: '#ff62ffff', rgba: [255, 98, 255, 255] },
  { dbz: 63, color: '#ff58ffff', rgba: [255, 88, 255, 255] },
  { dbz: 64, color: '#ff4effff', rgba: [255, 78, 255, 255] },
  { dbz: 65, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 66, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 67, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 68, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 69, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 70, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 71, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 72, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 73, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 74, color: '#ffffffff', rgba: [255, 255, 255, 255] },
  { dbz: 75, color: '#00ff00ff', rgba: [0, 255, 0, 255] },
  { dbz: 76, color: '#00ff00ff', rgba: [0, 255, 0, 255] },
  { dbz: 77, color: '#00ff00ff', rgba: [0, 255, 0, 255] },
  { dbz: 78, color: '#00ff00ff', rgba: [0, 255, 0, 255] },
  { dbz: 79, color: '#00ff00ff', rgba: [0, 255, 0, 255] },
  { dbz: 80, color: '#00ff00ff', rgba: [0, 255, 0, 255] }
] as const;

function readPrefs(): { priceMode: PriceMode; fuelType: FuelType } {
  try {
    const parsed = JSON.parse(localStorage.getItem(FUEL_PREFERENCES_KEY) || '{}');
    return {
      priceMode: parsed.priceMode === 'eur_l' ? 'eur_l' : 'local',
      fuelType: (parsed.fuelType && fuelTypeLabels[parsed.fuelType as FuelType]) ? parsed.fuelType : 'diesel'
    };
  } catch {
    return { priceMode: 'local', fuelType: 'diesel' };
  }
}

function readCookie(name: string) {
  const value = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`))
    ?.split('=')
    .slice(1)
    .join('=');
  return value ? decodeURIComponent(value) : '';
}

function readUnits(): UnitSystem {
  const stored = localStorage.getItem(UNIT_PREFERENCES_KEY) || readCookie('units');
  return stored === 'imperial' ? 'imperial' : 'metric';
}

function readLanguage(): LanguageCode {
  const stored = (localStorage.getItem(LANGUAGE_PREFERENCES_KEY) || readCookie('language') || 'EN').toUpperCase();
  return languageCodes.includes(stored as LanguageCode) ? stored as LanguageCode : 'EN';
}

function readInitialView() {
  const match = location.hash.match(/^#([\d.]+)\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)(?:\/(-?\d+(?:\.\d+)?))?(?:\/(-?\d+(?:\.\d+)?))?/);
  if (!match) return { center: [2.073, 40.727] as [number, number], zoom: 7, bearing: 0, pitch: 0 };
  const zoom = Number(match[1]);
  const lat = Number(match[2]);
  const lng = Number(match[3]);
  const bearing = Number(match[4] || 0);
  const pitch = Number(match[5] || 0);
  if (![zoom, lat, lng, bearing, pitch].every(Number.isFinite)) {
    return { center: [2.073, 40.727] as [number, number], zoom: 7, bearing: 0, pitch: 0 };
  }
  return { center: [lng, lat] as [number, number], zoom, bearing, pitch };
}

function writeMapHash(hash: string) {
  if (location.hash !== hash) {
    history.replaceState(null, '', `${location.pathname}${location.search}${hash}`);
  }
}

function formatKm(value?: number) {
  return typeof value === 'number' ? `${value.toFixed(value >= 100 ? 0 : 1)} km` : '—';
}

function formatDistanceKm(value: number | undefined, units: UnitSystem) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (units === 'imperial') {
    const miles = value * 0.621371;
    return `${miles.toFixed(miles >= 100 ? 0 : 1)} mi`;
  }
  return formatKm(value);
}

function formatElevationM(value: number | undefined, units: UnitSystem) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (units === 'imperial') return `${Math.round(value * 3.28084)} ft`;
  return `${Math.round(value)} m`;
}

function formatTemperatureC(value: number, units: UnitSystem) {
  if (units === 'imperial') return `${Math.round((value * 9) / 5 + 32)} F`;
  return `${Math.round(value)} C`;
}

function formatHours(value?: number) {
  if (typeof value !== 'number') return '—';
  const h = Math.floor(value);
  const m = Math.round((value - h) * 60);
  return `${h}h ${m.toString().padStart(2, '0')}`;
}

function formatRadarTime(seconds?: number) {
  if (!seconds) return '';
  return new Date(seconds * 1000).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

function rainViewerTileUrl(frame: RainRadarFrame) {
  return `${frame.host}${frame.path}/512/{z}/{x}/{y}/2/1_1.png`;
}

function rainRateLm2hFromDbz(dbz: number) {
  const reflectivity = 10 ** (dbz / 10);
  return (reflectivity / 200) ** (1 / 1.6);
}

function formatRainRate(value: number) {
  if (value < 1) return value.toFixed(1);
  if (value < 10) return value.toFixed(1);
  return String(Math.round(value));
}

function nearestRainViewerDbz(r: number, g: number, b: number, a: number) {
  let bestDbz = 0;
  let bestScore = Infinity;
  rainViewerUniversalBlueStops.forEach((stop) => {
    const [sr, sg, sb, sa] = stop.rgba;
    const score = (r - sr) ** 2 + (g - sg) ** 2 + (b - sb) ** 2 + ((a - sa) * 0.35) ** 2;
    if (score < bestScore) {
      bestScore = score;
      bestDbz = stop.dbz;
    }
  });
  return bestDbz;
}

function rainLegendStops(maxDbz?: number) {
  const top = Math.max(10, Math.min(80, Math.ceil(maxDbz || 10)));
  const span = Math.max(1, top - 10);
  return rainViewerUniversalBlueStops
    .filter((stop) => stop.dbz >= 10 && stop.dbz <= top)
    .map((stop) => `${stop.color} ${(((stop.dbz - 10) / span) * 100).toFixed(3)}%`)
    .join(', ');
}

function trafficEtaText(item?: TrafficEta) {
  return formatLiveTrafficSummary(item);
}

function mapTextFont(theme: MapTheme | null) {
  return theme === 'gaia' ? ['Open_Sans-Regular'] : ['Noto Sans Regular'];
}

function fuelGeoJsonUrl(fuelType: FuelType) {
  return fuelType === 'diesel' ? '/fuel/fuel-prices.geojson' : `/fuel/fuel-prices-${fuelType}.geojson`;
}

function parseLonLat(text: string): [number, number] | null {
  const match = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const a = Number(match[1]);
  const b = Number(match[2]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return [b, a];
  if (Math.abs(a) <= 180 && Math.abs(b) <= 90) return [a, b];
  return null;
}

async function geocodePoint(text: string, mapCenter: [number, number] | null): Promise<[number, number]> {
  const trimmed = text.trim();
  const parsed = parseLonLat(trimmed);
  if (parsed) return parsed;
  if (!trimmed || /^mapa|situacion|situación|actual$/i.test(trimmed)) {
    if (mapCenter) return mapCenter;
  }
  if (/^mi ubicaci[oó]n|location$/i.test(trimmed)) {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve([position.coords.longitude, position.coords.latitude]),
        () => reject(new Error('Location denied')),
        { enableHighAccuracy: true }
      );
    });
  }
  const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(trimmed)}`);
  if (!response.ok) throw new Error('POI search unavailable');
  const results = await response.json();
  if (!Array.isArray(results) || !results[0]) throw new Error(`POI not found: ${trimmed}`);
  return [Number(results[0].lon), Number(results[0].lat)];
}

function distanceKm(coords: Coord3[], index: number) {
  let total = 0;
  for (let i = 1; i <= index; i += 1) {
    const [aLng, aLat] = coords[i - 1];
    const [bLng, bLat] = coords[i];
    const dLat = (bLat - aLat) * Math.PI / 180;
    const dLng = (bLng - aLng) * Math.PI / 180;
    const s1 = Math.sin(dLat / 2);
    const s2 = Math.sin(dLng / 2);
    const h = s1 * s1 + Math.cos(aLat * Math.PI / 180) * Math.cos(bLat * Math.PI / 180) * s2 * s2;
    total += 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }
  return total;
}

function sampleWeatherGrid(grid: WeatherGrid, lon: number, lat: number) {
  let gx = ((((lon - grid.lo1) % 360) + 360) % 360) / (grid.dx || 1);
  let gy = (grid.la1 - lat) / (grid.dy || 1);
  gy = Math.max(0, Math.min(grid.height - 1, gy));
  gx = Math.max(0, Math.min(grid.width - 1, gx));
  const x0 = Math.floor(gx);
  const x1 = (x0 + 1) % grid.width;
  const y0 = Math.floor(gy);
  const y1 = Math.min(y0 + 1, grid.height - 1);
  const fx = gx - x0;
  const fy = gy - y0;
  const values = grid.values;
  const v00 = values[y0 * grid.width + x0];
  const v10 = values[y0 * grid.width + x1];
  const v01 = values[y1 * grid.width + x0];
  const v11 = values[y1 * grid.width + x1];
  const value = v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
  return grid.unit === 'K' ? value - 273.15 : value;
}

function interpolateColor(stops: Array<[number, [number, number, number, number?]]>, value: number) {
  if (value <= stops[0][0]) return stops[0][1];
  if (value >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];
  for (let i = 1; i < stops.length; i += 1) {
    if (value <= stops[i][0]) {
      const [aValue, aColor] = stops[i - 1];
      const [bValue, bColor] = stops[i];
      const t = (value - aValue) / (bValue - aValue || 1);
      return aColor.map((channel, index) => {
        const start = channel ?? 255;
        return Math.round(start + ((bColor[index] ?? 255) - start) * t);
      }) as [number, number, number, number?];
    }
  }
  return stops[stops.length - 1][1];
}

function renderWeatherTile(grid: WeatherGrid, mode: 'temp' | 'precip' | 'pressure', tempRange: TempLegend = null) {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(size, size);
  for (let py = 0; py < size; py += 1) {
    const lat = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * py) / size)));
    for (let px = 0; px < size; px += 1) {
      const lon = -180 + (360 * px) / size;
      const value = sampleWeatherGrid(grid, lon, lat);
      const color = mode === 'temp' && tempRange
        ? interpolateColor([[tempRange.min, [49, 130, 206, 42]], [(tempRange.min + tempRange.max) / 2, [255, 237, 74, 112]], [tempRange.max, [217, 72, 1, 168]]], value)
        : interpolateColor(mode === 'temp' ? tempStops : mode === 'pressure' ? pressureStops : precipStops, value);
      const index = (py * size + px) * 4;
      img.data[index] = color[0];
      img.data[index + 1] = color[1];
      img.data[index + 2] = color[2];
      img.data[index + 3] = color[3] ?? (mode === 'precip' ? 220 : 130);
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL('image/png');
}

function normalizeLon(lon: number) {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

function temperatureIsolineLevels(tempRange: TempLegend, units: UnitSystem) {
  if (!tempRange) return [];
  const levels: Array<{ celsius: number; label: string }> = [];
  if (units === 'imperial') {
    const minF = Math.ceil(((tempRange.min * 9) / 5 + 32) / 9) * 9;
    const maxF = Math.floor(((tempRange.max * 9) / 5 + 32) / 9) * 9;
    for (let levelF = minF; levelF <= maxF; levelF += 9) levels.push({ celsius: ((levelF - 32) * 5) / 9, label: `${levelF} F` });
  } else {
    const start = Math.ceil(tempRange.min / 5) * 5;
    for (let level = start; level <= tempRange.max; level += 5) levels.push({ celsius: level, label: `${level} C` });
  }
  return levels;
}

function gridValueRange(grid: WeatherGrid): TempLegend {
  let min = Infinity;
  let max = -Infinity;
  const step = Math.max(1, Math.floor(grid.values.length / 240000));
  for (let i = 0; i < grid.values.length; i += step) {
    const raw = grid.values[i];
    const value = grid.unit === 'K' ? raw - 273.15 : raw;
    if (!Number.isFinite(value)) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min: Math.floor(min), max: Math.ceil(max) };
}

function smoothLine(coords: [number, number][]) {
  if (coords.length < 3) return coords;
  let result = coords;
  for (let pass = 0; pass < 2; pass += 1) {
    const next: [number, number][] = [result[0]];
    for (let i = 0; i < result.length - 1; i += 1) {
      const a = result[i];
      const b = result[i + 1];
      next.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25]);
      next.push([a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
    }
    next.push(result[result.length - 1]);
    result = next;
  }
  return result;
}

function chainLineSegments(segments: Array<{ level: number; label: string; coordinates: [[number, number], [number, number]] }>) {
  const keyFor = (point: [number, number]) => `${point[0].toFixed(4)},${point[1].toFixed(4)}`;
  const endpointIndex = new Map<string, number[]>();
  segments.forEach((segment, index) => {
    segment.coordinates.forEach((point) => {
      const key = `${segment.label}|${keyFor(point)}`;
      const list = endpointIndex.get(key) || [];
      list.push(index);
      endpointIndex.set(key, list);
    });
  });
  const unused = new Set(segments.map((_, index) => index));
  const lines: Array<{ level: number; label: string; coordinates: [number, number][] }> = [];
  const takeTouching = (label: string, point: [number, number]) => {
    const list = endpointIndex.get(`${label}|${keyFor(point)}`) || [];
    while (list.length) {
      const index = list.pop()!;
      if (unused.has(index)) return index;
    }
    return -1;
  };
  while (unused.size) {
    const seedIndex = unused.values().next().value as number;
    unused.delete(seedIndex);
    const seed = segments[seedIndex];
    const line = [...seed.coordinates];
    let changed = true;
    while (changed) {
      changed = false;
      const endIndex = takeTouching(seed.label, line[line.length - 1]);
      if (endIndex >= 0) {
        unused.delete(endIndex);
        const [a, b] = segments[endIndex].coordinates;
        if (keyFor(a) === keyFor(line[line.length - 1])) {
          line.push(b);
        } else {
          line.push(a);
        }
        changed = true;
      }
      const startIndex = takeTouching(seed.label, line[0]);
      if (startIndex >= 0) {
        unused.delete(startIndex);
        const [a, b] = segments[startIndex].coordinates;
        if (keyFor(b) === keyFor(line[0])) {
          line.unshift(a);
        } else {
          line.unshift(b);
        }
        changed = true;
      }
    }
    lines.push({ level: seed.level, label: seed.label, coordinates: smoothLine(line) });
  }
  return lines;
}

function buildTemperatureIsolines(grid: WeatherGrid, units: UnitSystem): GeoJSON.FeatureCollection {
  const globalRange = gridValueRange(grid);
  const levels = temperatureIsolineLevels(globalRange, units);
  const segments: Array<{ level: number; label: string; coordinates: [[number, number], [number, number]] }> = [];
  if (!levels.length) return { type: 'FeatureCollection', features: [] };

  const stride = Math.max(1, Math.ceil(grid.width / 720));
  const cols = Math.floor(grid.width / stride);
  const rows = Math.floor((grid.height - 1) / stride);
  const lonStep = (grid.dx || 1) * stride;
  const latStep = (grid.dy || 1) * stride;
  const sample = (col: number, row: number) => {
    const lon = grid.lo1 + col * lonStep;
    const lat = grid.la1 - row * latStep;
    return {
      lng: normalizeLon(lon),
      lat,
      value: sampleWeatherGrid(grid, lon, lat)
    };
  };
  const pointAt = (
    a: ReturnType<typeof sample>,
    b: ReturnType<typeof sample>,
    level: number
  ): [number, number] => {
    const t = (level - a.value) / (b.value - a.value || 1);
    return [
      normalizeLon(a.lng + (b.lng - a.lng) * t),
      a.lat + (b.lat - a.lat) * t
    ];
  };

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const values = [
        sample(col, row),
        sample((col + 1) % cols, row),
        sample((col + 1) % cols, row + 1),
        sample(col, row + 1)
      ];
      levels.forEach(({ celsius, label }) => {
        const points: [number, number][] = [];
        for (let i = 0; i < 4; i += 1) {
          const j = (i + 1) % 4;
          const a = values[i];
          const b = values[j];
          if ((a.value < celsius && b.value >= celsius) || (a.value >= celsius && b.value < celsius)) {
            points.push(pointAt(a, b, celsius));
          }
        }
        for (let i = 0; i + 1 < points.length; i += 2) {
          const a = points[i];
          const b = points[i + 1];
          if (Math.abs(a[0] - b[0]) > 180) continue;
          segments.push({ level: Math.round(celsius * 10) / 10, label, coordinates: [a, b] });
        }
      });
    }
  }
  const features = chainLineSegments(segments)
    .filter((line) => line.coordinates.length >= 2)
    .map((line): GeoJSON.Feature<GeoJSON.LineString, { level_c: number; label: string }> => ({
      type: 'Feature',
      properties: { level_c: line.level, label: line.label },
      geometry: { type: 'LineString', coordinates: line.coordinates }
    }));
  return { type: 'FeatureCollection', features };
}

function createCelestialStars(count: number): CelestialStar[] {
  return Array.from({ length: count }, () => {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    return {
      x: Math.sin(phi) * Math.cos(theta),
      y: Math.sin(phi) * Math.sin(theta),
      z: Math.cos(phi),
      size: Math.random() * 0.68 + 0.13,
      opacity: Math.random() * 0.48 + 0.32,
      phase: Math.random() * Math.PI * 2,
      twinkle: Math.random() * 0.08
    };
  });
}

function projectCelestialStar(star: CelestialStar, view: StarfieldView, width: number, height: number) {
  const yaw = ((view.lng + view.bearing * 0.25) * Math.PI) / 180;
  const pitch = (((-view.lat * 0.72) + view.pitch * 0.35) * Math.PI) / 180;
  const roll = (view.bearing * Math.PI) / 180;
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);
  const cosRoll = Math.cos(roll);
  const sinRoll = Math.sin(roll);

  const yawX = star.x * cosYaw + star.z * sinYaw;
  const yawY = star.y;
  const yawZ = -star.x * sinYaw + star.z * cosYaw;
  const pitchX = yawX;
  const pitchY = yawY * cosPitch - yawZ * sinPitch;
  const pitchZ = yawY * sinPitch + yawZ * cosPitch;
  if (pitchZ <= 0.08) return null;

  const rollX = pitchX * cosRoll - pitchY * sinRoll;
  const rollY = pitchX * sinRoll + pitchY * cosRoll;
  const focal = Math.min(width, height) * 0.82;
  const x = width / 2 + (rollX / pitchZ) * focal;
  const y = height / 2 - (rollY / pitchZ) * focal;
  const margin = 8;
  if (x < -margin || x > width + margin || y < -margin || y > height + margin) return null;

  return {
    x,
    y,
    scale: Math.max(0.42, Math.min(1.06, 0.6 + pitchZ * 0.3))
  };
}

function StarField({ view }: { view: StarfieldView }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef(view);
  const drawRef = useRef<(() => void) | null>(null);
  const [reduceMotion, setReduceMotion] = useState(reducedMotionRequested);

  useEffect(() => {
    viewRef.current = view;
    if (reduceMotion) drawRef.current?.();
  }, [reduceMotion, view]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(query.matches);
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    const stars = createCelestialStars(16000);
    let tick = 0;
    let frame = 0;
    const draw = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const ratio = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      const pixelWidth = Math.round(width * ratio);
      const pixelHeight = Math.round(height * ratio);
      if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
      if (canvas.height !== pixelHeight) canvas.height = pixelHeight;
      if (canvas.style.width !== `${width}px`) canvas.style.width = `${width}px`;
      if (canvas.style.height !== `${height}px`) canvas.style.height = `${height}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      const currentView = viewRef.current;
      stars.forEach((star) => {
        const projected = projectCelestialStar(star, currentView, width, height);
        if (!projected) return;
        const alpha = Math.max(0.15, Math.min(1, star.opacity + Math.sin(tick + star.phase) * star.twinkle));
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, star.size * projected.scale, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(2)})`;
        ctx.fill();
      });
      tick += 0.008;
      if (!reduceMotion) frame = window.requestAnimationFrame(draw);
    };
    drawRef.current = draw;
    draw();
    if (reduceMotion) window.addEventListener('resize', draw);
    return () => {
      drawRef.current = null;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', draw);
    };
  }, [reduceMotion]);

  return <canvas ref={canvasRef} className="stars" aria-hidden="true" />;
}

function FuelHistogram({ data }: { data: FuelHistogramState }) {
  if (!data || data.prices.length < 3) return null;
  const prices = [...data.prices].sort((a, b) => a - b);
  const count = prices.length;
  const lo = prices[0];
  const hi = prices[count - 1];
  const spread = Math.max(0.01, hi - lo);
  const median = count % 2 === 0 ? (prices[count / 2 - 1] + prices[count / 2]) / 2 : prices[Math.floor(count / 2)];
  const bins = Array.from({ length: 30 }, () => 0);
  prices.forEach((price) => {
    const index = Math.min(Math.floor(((price - lo) / spread) * bins.length), bins.length - 1);
    bins[index] += 1;
  });
  const maxBin = Math.max(...bins, 1);
  const w = 220;
  const h = 90;
  const pad = { top: 14, bottom: 18, left: 4, right: 4 };
  const barW = (w - pad.left - pad.right) / bins.length;
  const chartH = h - pad.top - pad.bottom;
  const unit = data.mixedUnits ? '' : ' EUR/L';
  const medianX = pad.left + ((median - lo) / spread) * (w - pad.left - pad.right);

  return (
    <svg className="fuel-histogram" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Visible fuel price distribution">
      <rect width={w} height={h} rx="6" fill="rgba(0,0,0,0.72)" />
      {bins.map((bin, index) => {
        if (!bin) return null;
        const barH = (bin / maxBin) * chartH;
        const t = index / (bins.length - 1);
        const color = t < 0.5
          ? `rgb(${Math.round(255 * (t * 2))},200,30)`
          : `rgb(255,${Math.round(200 * (1 - (t - 0.5) * 2))},30)`;
        return (
          <rect
            key={index}
            x={pad.left + index * barW}
            y={pad.top + chartH - barH}
            width={Math.max(1, barW - 1)}
            height={barH}
            fill={color}
          />
        );
      })}
      <line x1={medianX} y1={pad.top} x2={medianX} y2={pad.top + chartH} stroke="#ffffff" strokeWidth="2" strokeDasharray="4 3" />
      <text x={w / 2} y="11" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="700">
        {`median ${median.toFixed(3)}${unit} (${count} stations)`}
      </text>
      <text x={pad.left} y={h - 4} fill="#ffffff" fontSize="10">{`${lo.toFixed(2)}${unit}`}</text>
      <text x={w - pad.right} y={h - 4} textAnchor="end" fill="#ffffff" fontSize="10">{`${hi.toFixed(2)}${unit}`}</text>
    </svg>
  );
}

export function App() {
  const journeyLauncherRef = useRef<HTMLButtonElement | null>(null);
  const toolsLauncherRef = useRef<HTMLButtonElement | null>(null);
  const mapRef = useRef<MapAdapter | null>(null);
  const geolocateWatch = useRef<number | null>(null);
  const followAnchor = useRef<{ x: number; y: number } | null>(null);
  const initialRouteFitDone = useRef(false);
  const programmaticMove = useRef(false);
  const userMarker = useRef<MapMarkerHandle | null>(null);
  const trackingRef = useRef<TrackingMode>('off');
  const updateFuelHistogramRef = useRef<() => void>(() => undefined);
  const appliedThemeRef = useRef<MapTheme | null>(null);
  const maneuverProgressRef = useRef<FocusedManeuverProgress | null>(null);
  const prefs = useMemo(readPrefs, []);
  const initialUnits = useMemo(readUnits, []);
  const initialLanguage = useMemo(readLanguage, []);
  const initialThemePreference = useMemo(() => readTheme(location.search), []);
  const initialUiMode = useMemo<UiMode>(() => initialThemePreference.mode, [initialThemePreference.mode]);
  const initialBasemapOverride = useMemo<BasemapOverride | null>(
    () => initialThemePreference.override,
    [initialThemePreference.override]
  );
  const initialPrefersDark = useMemo(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
    []
  );
  const initialResolvedTheme = useMemo(
    () => resolveTheme(initialUiMode, initialPrefersDark, initialBasemapOverride),
    [initialBasemapOverride, initialPrefersDark, initialUiMode]
  );
  const initialTheme = useMemo<MapTheme>(
    () => initialResolvedTheme.basemap === 'satellite'
      ? initialResolvedTheme.pairedBasemap
      : initialResolvedTheme.basemap,
    [initialResolvedTheme]
  );
  const initialView = useMemo(readInitialView, []);
  const [routes, setRoutes] = useState<RouteFeature[]>([]);
  const [fuelData, setFuelData] = useState<FuelGeoJson | null>(null);
  const [tollData, setTollData] = useState<TollGeoJson | null>(null);
  const [activeRoutes, setActiveRoutes] = useState<number[]>([]);
  const [focusedRouteNum, setFocusedRouteNum] = useState<number | null>(null);
  const [journeyState, dispatchJourney] = useReducer(reduceJourney, initialJourneyState);
  const [expandedJourneyTab, setExpandedJourneyTab] = useState<JourneyTab>('profile');
  const previousFocusedRouteKeyRef = useRef<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [uiMode, setUiMode] = useState<UiMode>(initialUiMode);
  const [basemapOverride, setBasemapOverride] = useState<BasemapOverride | null>(initialBasemapOverride);
  const [prefersDark, setPrefersDark] = useState(initialPrefersDark);
  const [fuelActive, setFuelActive] = useState(false);
  const [fuelMode, setFuelMode] = useState<'relative' | 'absolute'>('relative');
  const [fuelRange, setFuelRange] = useState({ min: 1.2, max: 2.2 });
  const [routeBufferOn, setRouteBufferOn] = useState(false);
  const [routeBufferKm, setRouteBufferKm] = useState(5);
  const [fuelStats, setFuelStats] = useState<{ min: number; max: number; mixedUnits: boolean } | null>(null);
  const [fuelHistogram, setFuelHistogram] = useState<FuelHistogramState>(null);
  const [tollsActive, setTollsActive] = useState(false);
  const [weather, setWeather] = useState({ temp: false, precip: false, wind: false });
  const [windLevel, setWindLevel] = useState<WindLevel>('surface');
  const [pressureActive, setPressureActive] = useState(false);
  const [terrain, setTerrain] = useState({ hillshade: true, terrain3d: false, contours: true });
  const [traffic, setTraffic] = useState<{ flow: boolean; eta: boolean; style: TrafficStyle }>({ flow: false, eta: false, style: 'relative0' });
  const [trafficEta, setTrafficEta] = useState<Record<number, TrafficEta>>({});
  const [baustellenActive, setBaustellenActive] = useState(false);
  const [baustellenData, setBaustellenData] = useState<{ type: 'FeatureCollection'; features: any[] } | null>(null);
  const [difficulty, setDifficulty] = useState<{ active: boolean; mode: DifficultyMode }>({ active: false, mode: 'slope' });
  const [difficultyLayerData, setDifficultyLayerData] = useState<{
    segments: { type: 'FeatureCollection'; features: any[] };
    hairpins: { type: 'FeatureCollection'; features: any[] };
  } | null>(null);
  const [difficultySummary, setDifficultySummary] = useState<Record<number, DifficultySummary>>({});
  const [routeInputs, setRouteInputs] = useState<RouteInputs>({ origin: uiText[initialLanguage].myLocation, destination: '', via: [''] });
  const [calculatingRoute, setCalculatingRoute] = useState(false);
  const [weatherData, setWeatherData] = useState<WeatherState>({});
  const [tempLegend, setTempLegend] = useState<TempLegend>(null);
  const [rainRadar, setRainRadar] = useState<RainRadarFrame | null>(null);
  const [rainRadarCoverage, setRainRadarCoverage] = useState<RainRadarCoverage>(null);
  const [rainRadarSamples, setRainRadarSamples] = useState<RainRadarSamples>({});
  const [viewportTick, setViewportTick] = useState(0);
  const [tracking, setTracking] = useState<TrackingMode>('off');
  const [userLocation, setUserLocation] = useState<Coord3 | null>(null);
  const [priceMode, setPriceMode] = useState<PriceMode>(prefs.priceMode);
  const [fuelType, setFuelType] = useState<FuelType>(prefs.fuelType);
  const [profileColorMode, setProfileColorMode] = useState<ProfileColorMode>('elevation');
  const [profileDynamic, setProfileDynamic] = useState(false);
  const [units, setUnits] = useState<UnitSystem>(initialUnits);
  const [language, setLanguage] = useState<LanguageCode>(initialLanguage);
  const [status, setStatus] = useState<string>(uiText[initialLanguage].loadingMap);
  const [starfieldView, setStarfieldView] = useState<StarfieldView>({
    lng: initialView.center[0],
    lat: initialView.center[1],
    bearing: initialView.bearing,
    pitch: initialView.pitch
  });

  const resolvedTheme = useMemo(
    () => resolveTheme(uiMode, prefersDark, basemapOverride),
    [basemapOverride, prefersDark, uiMode]
  );
  const satelliteActive = resolvedTheme.basemap === 'satellite';
  const theme = (satelliteActive ? resolvedTheme.pairedBasemap : resolvedTheme.basemap) as MapTheme;

  const t = useCallback((key: UiTextKey) => uiText[language][key] || uiText.EN[key], [language]);
  const localizedTrafficStyles = useMemo(
    () => trafficStyles.map((item) => ({ ...item, label: trafficStyleLabelsByLanguage[language][item.value] })),
    [language]
  );
  const localizedDifficultyModes = useMemo(
    () => difficultyModes.map((item) => ({ ...item, label: difficultyModeLabelsByLanguage[language][item.value] })),
    [language]
  );
  const selectTheme = useCallback((nextTheme: MapTheme | 'satellite' | 'paired') => {
    if (nextTheme === 'paired') {
      setBasemapOverride(null);
      return;
    }
    if (nextTheme === 'satellite') {
      setBasemapOverride('satellite');
      return;
    }
    if (nextTheme === 'gaia') {
      setUiMode('day');
      setBasemapOverride(null);
      return;
    }
    if (nextTheme === 'grayscale') {
      setUiMode('night');
      setBasemapOverride(null);
      return;
    }
    setBasemapOverride(nextTheme as BasemapOverride);
  }, []);
  const selectFuelType = useCallback((value: FuelType) => {
    selectAndActivateFuelType(value, setFuelType, setFuelActive);
  }, []);
  const closeTools = useCallback(() => dispatchJourney({ type: 'ESCAPE' }), []);
  const localizedProfileModeLabels = useMemo(() => profileModeLabelsByLanguage[language], [language]);

  useEffect(() => {
    const defaultOrigins: string[] = languageCodes.map((code) => uiText[code].myLocation);
    setRouteInputs((current) => (
      defaultOrigins.includes(current.origin) ? { ...current, origin: t('myLocation') } : current
    ));
  }, [language, t]);

  const activeRouteFeatures = useMemo(
    () => orderActiveRouteFeatures(routes, activeRoutes, focusedRouteNum),
    [routes, activeRoutes, focusedRouteNum]
  );
  const focusedRoute = activeRouteFeatures[0] ?? null;
  const focusedRouteKey = useMemo(
    () => focusedRoute ? fingerprintManeuverRoute(focusedRoute) : null,
    [focusedRoute]
  );
  useEffect(() => {
    setExpandedJourneyTab((current) => tabAfterFocusedRouteChange(
      current,
      previousFocusedRouteKeyRef.current,
      focusedRouteKey
    ));
    previousFocusedRouteKeyRef.current = focusedRouteKey;
  }, [focusedRouteKey]);
  const focusedManeuver = useMemo(() => {
    if (!focusedRoute || !focusedRouteKey) return null;
    return resolveNextInstruction(
      focusedRoute,
      focusedRoute.properties.instructions || [],
      tracking === 'follow' ? userLocation : null,
      maneuverProgressForRoute(maneuverProgressRef.current, focusedRouteKey)
    );
  }, [focusedRoute, focusedRouteKey, tracking, userLocation]);
  const focusedProfileProgressKm = focusedManeuver && (focusedManeuver.status === 'next' || focusedManeuver.status === 'finished')
    ? focusedManeuver.progressM / 1000
    : undefined;
  const profileViewport = useMemo<ProfileViewport>(() => {
    void viewportTick;
    return {
      width: 0,
      height: 190,
      isVisible: (coordinate, bottomInset) => {
        const camera = mapRef.current?.camera;
        return camera ? camera.isVisible([coordinate[0], coordinate[1]], bottomInset) : true;
      }
    };
  }, [mapReady, viewportTick]);
  const profileTemperature = useMemo<ProfileTemperature | undefined>(() => (
    weatherData.temp && tempLegend
      ? {
        range: tempLegend,
        sample: (coordinate) => sampleWeatherGrid(weatherData.temp!, coordinate[0], coordinate[1])
      }
      : undefined
  ), [tempLegend, weatherData.temp]);
  const setMapProfileCursor = useCallback((cursor: ProfileCursor | null) => {
    const map = mapRef.current;
    if (!map) return;
    map.routes.setCursor(cursor ? { mapFeature: cursor.mapFeature, textFont: mapTextFont(appliedThemeRef.current) } : null);
  }, []);

  useEffect(() => {
    if (!focusedRouteKey || !focusedManeuver) {
      maneuverProgressRef.current = null;
      return;
    }
    if (
      (focusedManeuver.status === 'next' || focusedManeuver.status === 'finished')
      && focusedManeuver.progressM !== null
    ) {
      maneuverProgressRef.current = persistManeuverProgress(
        maneuverProgressRef.current,
        focusedRouteKey,
        focusedManeuver.progressM
      );
    }
  }, [focusedManeuver, focusedRouteKey]);

  const routeContext = useMemo<RouteContext | null>(() => {
    if (!focusedRoute || !focusedManeuver) return null;
    const routeNum = focusedRoute.properties.route_num;
    const liveTraffic = trafficEta[routeNum];
    const normalizedTraffic = normalizeLiveTraffic(liveTraffic);
    const routeRain = Object.values(rainRadarSamples[routeNum] || {}).filter(Number.isFinite);
    const echoPoints = routeRain.filter((sample) => sample > 0).length;
    const progressKm = focusedManeuver.progressM === null ? 0 : focusedManeuver.progressM / 1_000;
    const routeTolls = (tollData?.features || [])
      .filter((feature) => Number(feature.properties.route_num) === routeNum)
      .map((feature) => ({
        km: Number(feature.properties.km),
        role: String(feature.properties.toll_role_label || feature.properties.toll_role || 'Unknown toll role'),
        confidence: String(feature.properties.toll_role_confidence || 'unknown')
      }))
      .filter((item) => Number.isFinite(item.km) && item.km >= progressKm)
      .sort((left, right) => left.km - right.km);
    const difficultyForRoute = difficultySummary[routeNum];
    const unavailableTollRoutes = tollData?.metadata?.unavailable_route_nums || [];
    const scopedFuel = fuelActive && routeBufferOn && fuelData
      ? filterFuelNearRoutes(fuelData, [focusedRoute], routeBufferKm)
      : null;

    return {
      maneuver: focusedManeuver,
      traffic: normalizedTraffic,
      rain: routeRain.length > 0 && echoPoints > 0
        ? { sampledPoints: routeRain.length, echoPoints, maxSignal: Math.max(...routeRain) }
        : undefined,
      toll: !unavailableTollRoutes.includes(routeNum) && routeTolls.length > 0
        ? {
            plazaCount: routeTolls.length,
            nearestAheadKm: Math.max(0, routeTolls[0].km - progressKm),
            nextRole: routeTolls[0].role,
            source: tollData?.metadata?.source || 'OpenStreetMap via Overpass API',
            nextRoleConfidence: routeTolls[0].confidence
          }
        : undefined,
      // Baustellen data currently lives only in the map source and has no truthful route-distance state.
      works: undefined,
      elevation: {
        ascentM: Number(focusedRoute.properties.ascend_m || 0),
        source: 'GraphHopper route elevation'
      },
      difficulty: difficultyForRoute ? {
        steepClimbKm: difficultyForRoute?.steep_climb_km,
        hairpinCount: difficultyForRoute?.hairpin_count,
        source: 'Route geometry analysis',
        confidence: 'inferred'
      } : undefined,
      fuel: scopedFuel && scopedFuel.features.length > 0
        ? { stationCount: scopedFuel.features.length, corridorKm: routeBufferKm }
        : undefined
    };
  }, [difficultySummary, focusedManeuver, focusedRoute, fuelActive, fuelData, rainRadarSamples, routeBufferKm, routeBufferOn, tollData, trafficEta]);
  const routeContextCards = useMemo(
    () => routeContext ? rankRouteContext(routeContext) : [],
    [routeContext]
  );
  const focusedRouteSummary = useMemo(() => focusedRoute
    ? buildRouteSummary(
      focusedRoute,
      focusedManeuver?.progressM ?? null,
      routeContext?.traffic?.travelTimeS,
      language,
      units
    )
    : null, [focusedManeuver?.progressM, focusedRoute, language, routeContext?.traffic?.travelTimeS, units]);

  useEffect(() => {
    if (!fuelStats || fuelStats.mixedUnits || fuelStats.min >= fuelStats.max) return;
    setFuelRange({ min: Math.floor(fuelStats.min * 100) / 100, max: Math.ceil(fuelStats.max * 100) / 100 });
  }, [fuelStats?.min, fuelStats?.max, fuelStats?.mixedUnits, fuelType]);

  useEffect(() => {
    setFuelHistogram(null);
  }, [fuelType]);

  useEffect(() => {
    persistFuelPreferences(priceMode, fuelType);
  }, [priceMode, fuelType]);

  useEffect(() => {
    persistUnitPreference(units);
  }, [units]);

  useEffect(() => {
    persistLanguagePreference(language);
  }, [language]);

  useEffect(() => {
    trackingRef.current = tracking;
  }, [tracking]);

  useEffect(() => () => cleanupTrackingRuntime({
    watch: geolocateWatch,
    marker: userMarker,
    followAnchor,
    updateFuelHistogram: updateFuelHistogramRef
  }, (watchId) => navigator.geolocation.clearWatch(watchId)), []);

  const updateFuelHistogram = useCallback(() => {
    mapRef.current?.journey.refreshFuelViewport();
  }, []);

  useEffect(() => {
    updateFuelHistogramRef.current = updateFuelHistogram;
  }, [updateFuelHistogram]);

  const applyFuelLayer = useCallback(() => {
    mapRef.current?.journey.applyFuel({
      data: fuelData,
      active: fuelActive,
      mode: fuelMode,
      range: fuelRange,
      fuelTypeLabel: fuelTypeLabels[fuelType],
      priceMode,
      corridor: { active: routeBufferOn, routes: activeRouteFeatures, bufferKm: routeBufferKm },
      textFont: mapTextFont(appliedThemeRef.current)
    });
  }, [activeRouteFeatures, fuelActive, fuelData, fuelMode, fuelRange, fuelType, priceMode, routeBufferKm, routeBufferOn]);

  const applyRoutes = useCallback((loadedRoutes: RouteFeature[]) => {
    mapRef.current?.routes.apply({
      routes: loadedRoutes,
      activeRouteNums: activeRoutes,
      profileColorMode,
      textFont: mapTextFont(appliedThemeRef.current)
    });
  }, [activeRoutes, profileColorMode]);

  const applyTolls = useCallback(() => {
    mapRef.current?.journey.applyTolls({ data: tollData, active: tollsActive, activeRouteNums: activeRoutes, textFont: mapTextFont(appliedThemeRef.current) });
  }, [activeRoutes, tollData, tollsActive]);

  const loadWeatherGrid = useCallback(async (kind: 'temp' | 'pressure') => {
    const key = kind;
    if (weatherData[key]) return weatherData[key];
    const url = kind === 'temp' ? '/weather/temp-surface.json' : '/weather/pressure.json';
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${kind} weather unavailable`);
    const json = await response.json();
    const obj = json[0];
    const grid: WeatherGrid = {
      width: obj.header.nx,
      height: obj.header.ny,
      values: new Float32Array(obj.data),
      unit: kind === 'temp' ? 'K' : 'raw',
      lo1: Number(obj.header.lo1 ?? 0),
      la1: Number(obj.header.la1 ?? 90),
      dx: Number(obj.header.dx ?? 1),
      dy: Number(obj.header.dy ?? 1)
    };
    setWeatherData((current) => ({ ...current, [key]: grid }));
    return grid;
  }, [weatherData]);

  const loadRainRadar = useCallback(async (announce = false) => {
    const response = await fetch(rainViewerApiUrl);
    if (!response.ok) throw new Error('RainViewer radar unavailable');
    const json = await response.json();
    const frames = Array.isArray(json?.radar?.past) ? json.radar.past : [];
    const latest = frames[frames.length - 1];
    if (!json?.host || !latest?.path || !latest?.time) {
      throw new Error('RainViewer radar frame missing');
    }
    const frame = {
      host: String(json.host),
      path: String(latest.path),
      time: Number(latest.time),
      generated: Number(json.generated || latest.time)
    };
    setRainRadar(frame);
    if (announce) setStatus(`${t('radarRain')} ${formatRadarTime(frame.time)}`);
    return frame;
  }, [t]);

  const applyTrafficFlow = useCallback(() => {
    mapRef.current?.journey.applyTraffic({ active: traffic.flow, style: traffic.style });
  }, [traffic.flow, traffic.style]);

  const applyBaustellen = useCallback(() => {
    mapRef.current?.journey.applyBaustellen({ active: baustellenActive, data: baustellenData, textFont: mapTextFont(appliedThemeRef.current) });
  }, [baustellenActive, baustellenData]);

  const applyDifficulty = useCallback(() => {
    mapRef.current?.journey.applyDifficulty({
      active: difficulty.active,
      mode: difficulty.mode,
      activeRouteNums: activeRoutes,
      segments: difficultyLayerData?.segments || null,
      hairpins: difficultyLayerData?.hairpins || null,
      textFont: mapTextFont(appliedThemeRef.current)
    });
  }, [activeRoutes, difficulty.active, difficulty.mode, difficultyLayerData]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(asset('routes-graphhopper.geojson')).then((r) => r.json()),
      fetch(fuelGeoJsonUrl(fuelType)).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(asset('routes-toll-booths.geojson')).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    ]).then(([routeJson, fuelJson, tollJson]: [RoutesGeoJson, FuelGeoJson | null, TollGeoJson | null]) => {
      if (cancelled) return;
      let dynamicRoutes: RouteFeature[] = [];
      try {
        const parsed = JSON.parse(localStorage.getItem(savedDynamicRoutesKey) || '[]');
        if (Array.isArray(parsed)) dynamicRoutes = parsed;
      } catch {
        dynamicRoutes = [];
      }
      setRoutes([...routeJson.features, ...dynamicRoutes]);
      setFuelData(fuelJson);
      setTollData(tollJson);
      setStatus(fuelJson ? `${fuelJson.metadata?.count || fuelJson.features.length} fuel stations` : 'Fuel unavailable');
    });
    return () => { cancelled = true; };
  }, [fuelType]);

  useEffect(() => {
    if (!weather.temp && !activeRouteFeatures.length) return;
    loadWeatherGrid('temp').catch(() => setStatus('Temperature unavailable'));
  }, [activeRouteFeatures.length, loadWeatherGrid, weather.temp]);

  useEffect(() => {
    if (!mapReady || rainRadar) return;
    let cancelled = false;
    loadRainRadar()
      .then((frame) => {
        if (!cancelled) setRainRadar(frame);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [loadRainRadar, mapReady, rainRadar]);

  useEffect(() => {
    if (!weather.precip || rainRadar) return;
    let cancelled = false;
    loadRainRadar(true)
      .then((frame) => {
        if (!cancelled) setRainRadar(frame);
      })
      .catch(() => {
        if (!cancelled) setStatus(`${t('rainRadar')} unavailable`);
      });
    return () => {
      cancelled = true;
    };
  }, [loadRainRadar, rainRadar, weather.precip]);

  useEffect(() => {
    mapRef.current?.weather.setEvents({
      onTemperatureLegend: setTempLegend,
      onRadarCoverage: (coverage) => {
        setRainRadarCoverage(coverage);
        if (coverage && (!coverage.maxDbz || coverage.maxDbz < 10)) {
          setStatus(t('noVisibleRain'));
        } else if (coverage && coverage.percent < 3) {
          setStatus(t('weakEchoes'));
        } else if (coverage) {
          setStatus(`${t('radarRain')} ${formatRadarTime(rainRadar?.time)}`);
        }
      },
      onUnavailable: (kind, error) => {
        console.warn(error);
        if (kind === 'wind') setStatus('Wind unavailable');
      }
    });
  }, [mapReady, rainRadar?.time, t]);

  useEffect(() => {
    if (!mapReady) return;
    void mapRef.current?.weather.refreshViewport();
  }, [mapReady, viewportTick, weather.precip, rainRadar]);

  useEffect(() => {
    if (!rainRadar || !activeRouteFeatures.length) {
      setRainRadarSamples({});
      return;
    }
    let cancelled = false;
    mapRef.current?.weather.sampleRoutes(
      { tileUrl: rainViewerTileUrl(rainRadar) },
      activeRouteFeatures.map((route) => ({ route_num: route.properties.route_num, coordinates: route.geometry.coordinates }))
    )
      .then((samples) => {
        if (!cancelled) setRainRadarSamples(samples);
      })
      .catch(() => {
        if (!cancelled) setRainRadarSamples({});
      });
    return () => {
      cancelled = true;
    };
  }, [activeRouteFeatures, rainRadar]);

  useEffect(() => {
    if (!pressureActive) return;
    loadWeatherGrid('pressure').catch(() => setStatus('Pressure unavailable'));
  }, [loadWeatherGrid, pressureActive]);

  useEffect(() => {
    if (!mapReady) return;
    mapRef.current?.journey.setEvents({
      onFuelStats: (stats) => setFuelStats(stats ? { min: stats.min, max: stats.max, mixedUnits: stats.mixedUnits } : null),
      onFuelHistogram: setFuelHistogram
    });
  }, [mapReady]);

  useEffect(() => {
    if (!baustellenActive || baustellenData) return;
    let cancelled = false;
    (async () => {
      try {
        let response = await fetch(asset('baustellen-muenchen-4w.geojson'));
        if (!response.ok) response = await fetch('https://geoportal.muenchen.de/geoserver/mor_wfs/ows?service=WFS&version=1.1.0&request=GetFeature&typeName=mor_wfs%3Abaustellen_opendata&outputFormat=application%2Fjson&srsName=EPSG%3A4326');
        if (!response.ok) throw new Error('Baustellen unavailable');
        const data = await response.json();
        if (!cancelled) setBaustellenData(data);
      } catch (error) {
        if (!cancelled) {
          console.warn(error);
          setStatus('Munich Baustellen unavailable');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [baustellenActive, baustellenData]);

  useEffect(() => {
    if (!difficulty.active || difficultyLayerData) return;
    let cancelled = false;
    Promise.all([
      fetch(asset('routes-difficulty.geojson')).then((response) => {
        if (!response.ok) throw new Error('Route difficulty unavailable');
        return response.json();
      }),
      fetch(asset('routes-hairpins.geojson')).then((response) => {
        if (!response.ok) throw new Error('Route hairpins unavailable');
        return response.json();
      }),
      fetch(asset('routes-difficulty-summary.json')).then((response) => {
        if (!response.ok) throw new Error('Route difficulty summary unavailable');
        return response.json();
      })
    ]).then(([segments, hairpins, summary]) => {
      if (cancelled) return;
      setDifficultyLayerData({ segments, hairpins });
      const byRoute: Record<number, DifficultySummary> = {};
      (summary.routes || []).forEach((item: DifficultySummary) => { byRoute[item.route_num] = item; });
      setDifficultySummary(byRoute);
    }).catch((error) => {
      if (!cancelled) {
        console.warn(error);
        setStatus('Route analysis unavailable');
      }
    });
    return () => { cancelled = true; };
  }, [difficulty.active, difficultyLayerData]);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    persistUiModePreference(uiMode);
  }, [uiMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    localStorage.setItem('beachlab.map.theme', theme);
	    if (appliedThemeRef.current === theme) return;
	    appliedThemeRef.current = theme;
	    map.setStyle(asset(`style-${theme}.json`));
    setStatus(`${mapThemes.find((item) => item.value === theme)?.label || theme} theme`);
  }, [mapReady, theme]);

  useEffect(() => {
    if (!mapReady) return;
    void mapRef.current?.terrain.apply({
      satelliteActive,
      hillshade: terrain.hillshade,
      terrain3d: terrain.terrain3d,
      contours: terrain.contours,
      textFont: mapTextFont(appliedThemeRef.current)
    });
  }, [mapReady, satelliteActive, terrain.contours, terrain.hillshade, terrain.terrain3d, theme]);

  useEffect(() => {
    if (!mapReady) return;
    applyRoutes(routes);
  }, [mapReady, routes, activeRoutes, applyRoutes]);

  useEffect(() => {
    if (!mapReady) return;
    applyFuelLayer();
  }, [mapReady, applyFuelLayer, viewportTick]);

  useEffect(() => {
    if (!mapReady) return;
    applyTolls();
  }, [mapReady, applyTolls]);

  useEffect(() => {
    if (!mapReady) return;
    const grid = weatherData.temp || null;
    mapRef.current?.weather.setTemperature({
      active: weather.temp,
      dataUrl: weather.temp && grid ? renderWeatherTile(grid, 'temp', tempLegend) : null,
      isolines: weather.temp && grid ? buildTemperatureIsolines(grid, units) : null,
      textFont: mapTextFont(appliedThemeRef.current),
      grid
    });
  }, [mapReady, tempLegend?.max, tempLegend?.min, units, weather.temp, weatherData.temp]);

  useEffect(() => {
    if (!mapReady) return;
    mapRef.current?.weather.setRadar({
      active: weather.precip,
      tileUrl: rainRadar ? rainViewerTileUrl(rainRadar) : null,
      frameKey: rainRadar?.time ?? null
    });
  }, [mapReady, rainRadar, weather.precip]);

  useEffect(() => {
    if (!mapReady) return;
    mapRef.current?.weather.setPressure({
      active: pressureActive,
      dataUrl: pressureActive && weatherData.pressure ? renderWeatherTile(weatherData.pressure, 'pressure') : null
    });
  }, [mapReady, pressureActive, weatherData.pressure]);

  useEffect(() => {
    if (!mapReady) return;
    applyTrafficFlow();
  }, [applyTrafficFlow, mapReady]);

  useEffect(() => {
    if (!mapReady || !traffic.eta || !activeRoutes.length) {
      setTrafficEta({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`/api/tomtom/routes/live?routes=${activeRoutes.join(',')}`);
        if (!response.ok) throw new Error('Live ETA unavailable');
        const json = await response.json();
        if (cancelled) return;
        const byRoute: Record<number, TrafficEta> = {};
        (json.routes || []).forEach((item: TrafficEta) => { byRoute[item.route_num] = item; });
        setTrafficEta(byRoute);
        setStatus('TomTom live ETA updated');
      } catch (error) {
        if (!cancelled) {
          console.warn(error);
          setStatus('TomTom live ETA unavailable');
        }
      }
    };
    load();
    const interval = window.setInterval(load, 120000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeRoutes, mapReady, traffic.eta]);

  useEffect(() => {
    if (!mapReady) return;
    applyBaustellen();
  }, [applyBaustellen, mapReady]);

  useEffect(() => {
    if (!mapReady) return;
    applyDifficulty();
  }, [applyDifficulty, mapReady]);

  useEffect(() => {
    // Basemap tile failures must not prevent the independent wind layer loading.
    void mapRef.current?.weather.setWind({ active: weather.wind, level: windLevel, dataUrl: `/weather/wind-${windLevel}.json` });
  }, [mapReady, weather.wind, windLevel]);

  const fitActiveRoutes = useCallback(() => {
    const camera = mapRef.current?.camera;
    const bounds = boundsForRoutes(activeRouteFeatures);
    if (camera && bounds) camera.fitRoutes(bounds);
  }, [activeRouteFeatures]);

  useEffect(() => {
    if (!mapReady || initialRouteFitDone.current || !activeRouteFeatures.length) return;
    if (location.hash) {
      initialRouteFitDone.current = true;
      return;
    }
    fitActiveRoutes();
    initialRouteFitDone.current = true;
  }, [activeRouteFeatures, fitActiveRoutes, mapReady]);

  const toggleRoute = (routeNum: number) => {
    const next = toggleRouteSelection(activeRoutes, routeNum);
    setActiveRoutes(next);
    setFocusedRouteNum((focused) => focused === routeNum
      ? next[0] ?? null
      : focused !== null && next.includes(focused) ? focused : next[0] ?? null);
    dispatchJourney({ type: next.length ? 'ROUTE_READY' : 'CLEAR_ROUTES' });
  };

  const focusRoute = (routeNum: number) => {
    setFocusedRouteNum(routeNum);
    setActiveRoutes((current) => current.includes(routeNum) ? current : [...current, routeNum]);
    dispatchJourney({ type: 'ROUTE_READY' });
  };

  const saveRoutes = () => {
    localStorage.setItem(savedRoutesKey, JSON.stringify(activeRoutes));
    localStorage.setItem(savedDynamicRoutesKey, JSON.stringify(routes.filter((route) => route.properties.dynamic)));
    setStatus(t('routesSaved'));
  };

  const loadSavedRoutes = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(savedRoutesKey) || '[]');
      const restored = normalizeSavedRouteSelection(saved, routes.map((route) => route.properties.route_num));
      if (!restored.length) {
        setStatus(t('noSavedRoutes'));
        return;
      }
      setActiveRoutes(restored);
      setFocusedRouteNum(restored[0]);
      dispatchJourney({ type: 'ROUTE_READY' });
    } catch {
      setStatus(t('noSavedRoutes'));
    }
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setStatus(t('locationUnavailable'));
      return;
    }
    const next: TrackingMode = tracking === 'off' ? 'locate' : tracking === 'locate' ? 'follow' : 'off';
    if (shouldStartNavigation(tracking, next, journeyState.phase, Boolean(focusedRoute))) {
      dispatchJourney({ type: 'START_NAVIGATION' });
    }
    setTracking(next);
    if (next === 'off') {
      if (geolocateWatch.current !== null) navigator.geolocation.clearWatch(geolocateWatch.current);
      geolocateWatch.current = null;
      setUserLocation(null);
      setStatus(t('locationOff'));
      return;
    }
    const map = mapRef.current;
    const onPosition = (position: GeolocationPosition) => {
      const lngLat: [number, number] = [position.coords.longitude, position.coords.latitude];
      setUserLocation([lngLat[0], lngLat[1], 0]);
      if (!map) return;
      if (!userMarker.current) {
        const el = document.createElement('div');
        el.className = 'user-location-dot';
        userMarker.current = map.camera.createMarker(el, lngLat);
      } else {
        userMarker.current.setLngLat(lngLat);
      }
      programmaticMove.current = true;
      if (next === 'follow') {
        const viewport = map.camera.viewport();
        const anchor = followAnchor.current || { x: viewport.width / 2, y: viewport.height / 2 };
        map.camera.follow(lngLat, anchor);
      } else {
        followAnchor.current = map.camera.locate(lngLat);
      }
    };
    if (geolocateWatch.current !== null) navigator.geolocation.clearWatch(geolocateWatch.current);
    geolocateWatch.current = navigator.geolocation.watchPosition(onPosition, () => setStatus(t('locationDenied')), { enableHighAccuracy: true });
    navigator.geolocation.getCurrentPosition(onPosition, () => setStatus(t('locationDenied')), { enableHighAccuracy: true });
  };

  const calculateDynamicRoute = async () => {
    if (!routeInputs.destination.trim()) {
      const message = t('destinationRequired');
      setStatus(message);
      throw new Error(message);
    }
    setCalculatingRoute(true);
    try {
      const mapCenter = mapRef.current?.camera.center() || null;
      const points = await Promise.all([
        geocodePoint(routeInputs.origin, mapCenter),
        ...routeInputs.via.filter((item) => item.trim()).map((item) => geocodePoint(item, mapCenter)),
        geocodePoint(routeInputs.destination, mapCenter)
      ]);
      const response = await fetch('/api/routes/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${routeInputs.origin || 'Origen'} - ${routeInputs.destination}`,
          points,
          labels: ['start', ...routeInputs.via.filter((item) => item.trim()).map((_, index) => `via ${index + 1}`), 'end']
        })
      });
      if (!response.ok) throw new Error(t('routeApiUnavailable'));
      const json = await response.json();
      if (json.status !== 'ok' || !json.feature?.geometry?.coordinates?.length) throw new Error(json.error || t('routeNotFound'));
      const feature = json.feature as RouteFeature;
      const routeNum = feature.properties.route_num || -Date.now();
      const color = routePalette[Math.abs(routeNum) % routePalette.length];
      feature.properties = {
        ...feature.properties,
        route_num: routeNum,
        color,
        dynamic: true,
        destination_label: routeInputs.destination.trim()
      };
      setRoutes((current) => [...current, feature]);
      setActiveRoutes((current) => current.includes(routeNum) ? current : [...current, routeNum]);
      setFocusedRouteNum(routeNum);
      dispatchJourney({ type: 'ROUTE_READY' });
      setStatus(t('dynamicRouteCalculated'));
      setTimeout(() => {
        const bounds = boundsForRoutes([feature]);
        if (bounds) mapRef.current?.camera.fitDynamicRoute(bounds);
      }, 100);
    } catch (error) {
      console.warn(error);
      const message = error instanceof Error ? error.message : t('routeCalculationFailed');
      setStatus(message);
      throw new Error(message);
    } finally {
      setCalculatingRoute(false);
    }
  };

  const rainLegendVisible = Boolean(weather.precip && rainRadarCoverage?.maxDbz && rainRadarCoverage.maxDbz >= 10);
  const rainLegendMaxDbz = Math.max(10, Math.min(80, Math.ceil(rainRadarCoverage?.maxDbz || 10)));
  const rainLegendMinRate = formatRainRate(rainRateLm2hFromDbz(10));
  const rainLegendMaxRate = formatRainRate(rainRateLm2hFromDbz(rainLegendMaxDbz));
  const rainLegendGradient = rainLegendStops(rainLegendMaxDbz);

  const journeyPlanPanel = (
    <>
      <div className="journey-planning">
        <RoutePlanner
          inputs={routeInputs}
          calculating={calculatingRoute}
          labels={{
            origin: t('originPlaceholder'),
            destination: t('destinationPlaceholder'),
            via: t('viaPlaceholder'),
            optional: t('optional'),
            addVia: t('addVia'),
            removeVia: t('remove'),
            calculate: t('calculateRoute'),
            calculating: t('calculating')
          }}
          onInputsChange={setRouteInputs}
          onCalculate={calculateDynamicRoute}
        />
        <div className="route-actions">
          <button type="button" onClick={fitActiveRoutes}>{t('fit')}</button>
          <button type="button" onClick={saveRoutes}>{t('save')}</button>
          <button type="button" onClick={loadSavedRoutes}>{t('load')}</button>
        </div>
      </div>
      <RouteAlternatives
        routes={routes}
        activeRouteNums={activeRoutes}
        focusedRouteNum={focusedRouteNum}
        labels={{
          alternatives: journeyUiLabels[language].alternatives,
          active: journeyUiLabels[language].active,
          inactive: journeyUiLabels[language].inactive,
          focused: journeyUiLabels[language].focused,
          focus: journeyUiLabels[language].focus,
          activate: journeyUiLabels[language].activate,
          deactivate: journeyUiLabels[language].deactivate
        }}
        formatMetrics={(route) => (
          <>
            {formatDistanceKm(route.properties.distance_km_precise || route.properties.distance_km, units)} · {formatHours(route.properties.duration_h)} · ↑{formatElevationM(route.properties.ascend_m || 0, units)}
            {trafficEtaText(trafficEta[route.properties.route_num]) ? ` · ${trafficEtaText(trafficEta[route.properties.route_num])}` : ''}
            {difficultySummary[route.properties.route_num] ? ` · ${difficultySummary[route.properties.route_num].hairpin_count || 0} U · ${formatDistanceKm(difficultySummary[route.properties.route_num].curvy_km || 0, units)} ${t('curves')}` : ''}
            {route.properties.dynamic ? ` · ${t('dynamicRoute')}` : ''}
          </>
        )}
        onToggleActive={toggleRoute}
        onFocus={focusRoute}
      />
    </>
  );
  const journeyRoadbookPanel = focusedRoute ? (
    <Roadbook
      label={roadbookLandmarkLabels[language]}
      instructions={focusedRoute.properties.instructions || []}
      activeIndex={focusedManeuver?.instructionIndex ?? null}
      language={language}
      units={units}
    />
  ) : null;
  const journeyProfilePanel = activeRouteFeatures.length > 0 ? (
    <section className="journey-profile-area" aria-label={t('elevationProfile')}>
      <Suspense fallback={<div className="profile-loading" role="status">{t('elevationProfile')}</div>}>
        <LazyExpandedProfile
          routes={activeRouteFeatures}
          layout="expanded"
          units={units}
          colorMode={profileColorMode}
          dynamic={profileDynamic}
          viewport={profileViewport}
          currentLocation={tracking === 'follow' ? userLocation : null}
          rainSamples={rainRadarSamples}
          rainActive={weather.precip}
          temperature={weather.temp ? profileTemperature : undefined}
          labels={{
            profile: t('elevationProfile'),
            dynamic: t('dynamic'),
            colorMode: t('profileColorMode'),
            noRain: t('noRadarEchoRoute'),
            max: t('max'),
            colorModes: localizedProfileModeLabels
          }}
          onColorModeChange={setProfileColorMode}
          onDynamicChange={setProfileDynamic}
          onCursor={setMapProfileCursor}
        />
      </Suspense>
    </section>
  ) : null;
  const expandedTabbedJourney = journeyState.snap === 'expanded'
    && (journeyState.phase === 'route_ready' || journeyState.phase === 'navigating');
  const journeyLabels = journeyUiLabels[language];

  return (
    <AppShell
      chromeMode={resolvedTheme.chromeMode}
      brandHref="https://beachlab.org"
      brandImageSrc={asset('icons/beachlab-logo.webp')}
      status={status}
      tracking={tracking}
      journeyText={activeRouteFeatures[0]?.properties.short_name || activeRouteFeatures[0]?.properties.name || 'New route'}
      labels={{ journey: t('journey'), location: t('myLocation'), tools: t('tools') }}
      journeyLauncherRef={journeyLauncherRef}
      toolsLauncherRef={toolsLauncherRef}
      onJourney={() => {
        if (journeyState.phase === 'explore') {
          dispatchJourney({ type: 'OPEN_PLANNER' });
          dispatchJourney({ type: 'SET_SNAP', snap: 'expanded' });
        }
        dispatchJourney({ type: 'OPEN_JOURNEY' });
      }}
      onLocation={handleLocate}
      onTools={() => {
        dispatchJourney({ type: 'OPEN_TOOLS' });
      }}
    >
      <StarField view={starfieldView} />
      <MapCanvas
        initialStyle={asset(`style-${initialTheme}.json`)}
        initialView={initialView}
        theme={theme}
        classifyRadarColor={nearestRainViewerDbz}
        onAdapter={(adapter) => {
          mapRef.current = adapter;
          adapter?.terrain.setEvents({
            onUnavailable: (_kind, error) => {
              console.warn(error);
              setStatus('Contours unavailable');
            }
          });
        }}
        onReady={() => {
          appliedThemeRef.current = initialTheme;
          setMapReady(true);
          setStatus(uiText[initialLanguage].mapReady);
          if (!location.hash) mapRef.current?.camera.fitInitial([[-0.5, 38.4], [4.6, 43.0]]);
        }}
        onMove={setStarfieldView}
        onMoveEnd={({ hash }) => {
          if (trackingRef.current === 'follow' && !programmaticMove.current && userMarker.current) {
            followAnchor.current = mapRef.current?.camera.project(userMarker.current.getLngLat()) || null;
          }
          programmaticMove.current = false;
          setViewportTick((value) => value + 1);
          updateFuelHistogramRef.current();
          writeMapHash(hash);
        }}
        onZoomEnd={({ hash }) => {
          setViewportTick((value) => value + 1);
          updateFuelHistogramRef.current();
          writeMapHash(hash);
        }}
        onIdle={() => updateFuelHistogramRef.current()}
        onBeforeRemove={() => {
          setMapReady(false);
        }}
      />

      <TravelSheet
        state={journeyState}
        title={t('journey')}
        labels={{ compact: journeyLabels.compact, medium: journeyLabels.medium, expanded: journeyLabels.expanded, close: t('closeJourney') }}
        restoreFocusRef={journeyLauncherRef}
        onEscape={() => dispatchJourney({ type: 'ESCAPE' })}
        onClose={() => dispatchJourney({ type: 'CLOSE_JOURNEY' })}
        onSnapChange={(snap) => dispatchJourney({ type: 'SET_SNAP', snap })}
      >
        {routeContext && focusedManeuver ? (
          <div className="journey-overview">
            <NextManeuver maneuver={focusedManeuver} language={language} units={units} />
            {journeyState.snap === 'compact' && focusedRouteSummary ? <RouteSummary summary={focusedRouteSummary} /> : null}
            <RouteContextCards cards={routeContextCards} />
            {journeyState.snap === 'compact' && focusedRoute ? (
              <CompactLongitudinalProfile
                routes={[focusedRoute]}
                units={units}
                colorMode={profileColorMode}
                viewport={profileViewport}
                progressKm={focusedProfileProgressKm}
                currentLocation={tracking === 'follow' ? userLocation : null}
                rainSamples={rainRadarSamples}
                rainActive={weather.precip}
                temperature={weather.temp ? profileTemperature : undefined}
                labels={{
                  profile: t('elevationProfile'),
                  dynamic: t('dynamic'),
                  colorMode: t('profileColorMode'),
                  noRain: t('noRadarEchoRoute'),
                  max: t('max'),
                  colorModes: localizedProfileModeLabels
                }}
                onCursor={setMapProfileCursor}
              />
            ) : null}
          </div>
        ) : null}
        {expandedTabbedJourney ? (
          <JourneyTabs
            selected={expandedJourneyTab}
            labels={journeyTabLabels[language]}
            panels={{ plan: journeyPlanPanel, roadbook: journeyRoadbookPanel, profile: journeyProfilePanel }}
            onSelect={setExpandedJourneyTab}
          />
        ) : (
          <>
            {journeyPlanPanel}
            {journeyState.snap === 'expanded' ? journeyRoadbookPanel : null}
            {journeyState.snap === 'expanded' ? journeyProfilePanel : null}
          </>
        )}
      </TravelSheet>

      {journeyState.surface === 'tools' ? <Suspense fallback={null}><LazyToolDrawer
        open={journeyState.surface === 'tools'}
        title={t('tools')}
        closeLabel={t('closeTools')}
        restoreFocusRef={toolsLauncherRef}
        map={{
          activeStyle: resolvedTheme.basemap as MapStyleChoice,
          terrain3d: terrain.terrain3d,
          contours: terrain.contours,
          hillshade: terrain.hillshade
        }}
        journey={{
          fuelActive,
          fuelType,
          tollsActive,
          traffic,
          baustellenActive,
          difficulty,
          fuelTypes: Object.entries(toolLabels[language].fuelTypes).map(([value, label]) => ({ value: value as FuelType, label })),
          trafficStyles: localizedTrafficStyles.map(({ value, label }) => ({ value, label })),
          difficultyModes: localizedDifficultyModes.map(({ value, label }) => ({ value, label }))
        }}
        weather={{ temperature: weather.temp, rain: weather.precip, wind: weather.wind, windLevel, pressure: pressureActive }}
        preferences={{
          fuelMode,
          units,
          fuelRange,
          fuelRangeBounds: fuelStats && !fuelStats.mixedUnits
            ? { min: Math.floor(fuelStats.min * 100) / 100, max: Math.ceil(fuelStats.max * 100) / 100 }
            : { min: 0, max: 20 },
          showFuelRange: Boolean(fuelStats && !fuelStats.mixedUnits),
          routeBufferOn,
          routeBufferKm,
          priceMode,
          language,
          uiMode,
          legacyHref: asset('legacy.html'),
          impressumHref: '/impressum'
        }}
        callbacks={{
          onClose: closeTools,
          onZoomIn: () => mapRef.current?.camera.zoomIn(),
          onZoomOut: () => mapRef.current?.camera.zoomOut(),
          onResetNorth: () => mapRef.current?.camera.resetNorth(),
          onSelectStyle: (style) => selectTheme(style),
          onTerrain3dChange: (active) => setTerrain((current) => ({ ...current, terrain3d: active })),
          onContoursChange: (active) => setTerrain((current) => ({ ...current, contours: active })),
          onHillshadeChange: (active) => setTerrain((current) => ({ ...current, hillshade: active })),
          onFuelActiveChange: setFuelActive,
          onFuelTypeChange: selectFuelType,
          onTollsChange: setTollsActive,
          onTrafficFlowChange: (active) => setTraffic((current) => ({ ...current, flow: active })),
          onTrafficStyleChange: (style) => setTraffic((current) => ({ ...current, style })),
          onTrafficEtaChange: (active) => setTraffic((current) => ({ ...current, eta: active })),
          onBaustellenChange: setBaustellenActive,
          onDifficultyActiveChange: (active) => setDifficulty((current) => ({ ...current, active })),
          onDifficultyModeChange: (mode) => setDifficulty((current) => ({ ...current, mode })),
          onTemperatureChange: (active) => setWeather((current) => ({ ...current, temp: active })),
          onRainChange: (active) => setWeather((current) => ({ ...current, precip: active })),
          onWindChange: (active) => setWeather((current) => ({ ...current, wind: active })),
          onWindLevelChange: setWindLevel,
          onPressureChange: setPressureActive,
          onFuelModeChange: setFuelMode,
          onUnitsChange: setUnits,
          onFuelRangeChange: setFuelRange,
          onRouteBufferOnChange: setRouteBufferOn,
          onRouteBufferKmChange: setRouteBufferKm,
          onPriceModeChange: setPriceMode,
          onLanguageChange: setLanguage,
          onUiModeChange: setUiMode
        }}
      /></Suspense> : null}

      <div className="legend-stack">
        {weather.temp && tempLegend && (
          <div className="weather-legend" aria-label={t('visibleTemperatureScale')}>
            <div>
              <span>{formatTemperatureC(tempLegend.min, units)}</span>
              <span>{formatTemperatureC(tempLegend.max, units)}</span>
            </div>
            <i />
          </div>
        )}

        {weather.precip && (
          <>
            {rainLegendVisible && (
              <div className="rain-legend" aria-label={t('rainIntensity')}>
                <span>10 dBZ<br />~{rainLegendMinRate} l/m²/h</span>
                <i aria-hidden="true" style={{ background: `linear-gradient(90deg, ${rainLegendGradient})` }}>
                  <span className="rain-scale"><b /><b /><b /><b /><b /><b /><b /></span>
                </i>
                <span>{rainLegendMaxDbz} dBZ<br />~{rainLegendMaxRate} l/m²/h</span>
              </div>
            )}
            <a className="rain-attribution" href="https://www.rainviewer.com/" target="_blank" rel="noreferrer">RainViewer</a>
          </>
        )}

        <FuelHistogram data={fuelHistogram} />
      </div>

    </AppShell>
  );
}
